import { useTranslation } from 'react-i18next';
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  Fragment,
} from 'react';
import {
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { API } from '../../API';
import { showRequestError } from '../../utils/requestError';
import { toApiAffiliationTypes } from '../../utils/affiliationTypes';
import { format, subDays, addDays } from 'date-fns';
import { enGB as en, uk } from 'date-fns/locale';

import { useCurrentUser } from '../../contexts/CurrentUser';
import { useCurrentQuota } from '../../contexts/CurrentQuota';
import Modal from '../../components/Modal';
import { Search } from '../../components/Search/Search';
import { QueryInput } from '../NarrativeAddPage/QueryInput/QueryInput';
import { NearMixedPairWarning } from '../../components/NearMixedPairWarning/NearMixedPairWarning';
import DatePickerWrapper from '../../components/Datepicker/DatepickerWrapper';
import { ExpandableText } from '../../components/CollapsibleText/ExpandableText';
import { ReactComponent as SaveIcon } from './assets/save.svg';
import { ReactComponent as ArrowIcon } from '../NarrativePage/assets/arrow.svg';
import { ReactComponent as ArrowDownIcon } from '../NarrativePage/assets/arrowDown.svg';
import { ReactComponent as SortIcon } from '../../assets/sort.svg';
import { ReactComponent as ChevronIcon } from '../../components/Filters/assets/chevron.svg';
import { ReactComponent as AddToIcon } from '../../assets/addTo.svg';
import { ReactComponent as CloseIcon } from './assets/close.svg';
import { ReactComponent as AnchorIcon } from '../../assets/anchor.svg';
import { LoaderSmall } from '../../components/LoaderSmall/LoaderSmall';
import { Loader } from '../../components/Loader/Loader';
import { DropdownMenu } from '../../components/DropdownMenu/DropdownMenu';
import { Radiobutton } from '../../components/DropdownMenu/Radiobutton';
import { SortingOrder } from '../../components/DropdownMenu/SortingOrder';
import { ampli } from '../../ampli';
import { MessageModal } from '../../components/MessagesTable/MessageModal';
import styles from './SearchPage.module.scss';
import messageStyles from '../../components/MessagesTable/ExtendedMessageView.module.scss';
import Tooltip from '../../components/Tooltip/Tooltip';
import { ReactComponent as AIIcon } from '../../assets/ai.svg';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { MessageTooltips } from '../../components/MessagesTable/MessageTooltips';

import { AddToNarrativeModal } from '../../components/AddToNarrativeModal/AddToNarrativeModal';
import { AddToSourceGroupModal } from '../../components/AddToSourceGroupModal/AddToSourceGroupModal';
import { CheckboxField } from '../../components/Checkbox/CheckboxField';
import { areAllIdsSelected } from '../NarrativePage/contexts/MessagesContext';

import { languageNumberMapping } from '../../utils/languages';
import { Feedback, feedbackContexts } from '../../components/Feedback/Feedback';
import Switch from '../../components/Switch';
import Select from 'react-select';
import { replaceNewlinesWithBreaks } from '../../utils/replaceNewlinesWithBreaks';
import { ImagesContainer } from '../../components/Image/ImagesContainer';
import { QuotaCheckModal } from '../../components/QuotaCheckModal/QuotaCheckModal';
import { QuotaCheckModalOld } from '../../components/QuotaCheckModalOld/QuotaCheckModalOld';
import { TranslationSwitch } from '../../components/TranslationSwitch/TranslationSwitch';
import {
  formatNumber,
  formatWithCustomGigaBillion,
} from '../../utils/fomatNumbers';
import { QueryAssistantModal } from '../../components/QueryAssistantModal/QueryAssistantModal';
import { ReactComponent as AIQueryIcon } from '../../assets/ai-stars.svg';
import SwitchSmall from '../../components/SwitchSmall';
import { SearchPageFilter } from './FilterNew/Filter';
import { useSearchContext, getEmptyFilterState } from './contexts';
import { CountriesChooserDropdown } from './FilterNew/DropdownFilters/CountriesChooserDropdown';
import { PlatformChooserDropdown } from './FilterNew/DropdownFilters/PlatformChooserDropdown';
import { ContentTypesChooserDropdown } from './FilterNew/DropdownFilters/ContentTypesChooserDropdown';
import { AffiliationCountriesDropdown } from './FilterNew/DropdownFilters/AffiliationCountriesDropdown';
import { AffiliationTypesChooserDropdown } from './FilterNew/DropdownFilters/AffiliationTypesChooserDropdown';
import { BotsChooserDropdown } from './FilterNew/DropdownFilters/BotsChooserDropdown';
import { IoCChooserDropdown } from './FilterNew/DropdownFilters/IoCChooserDropdown';
import { LanguageChooserDropdown } from './FilterNew/DropdownFilters/LanguageChooserDropdown';
import { FeedsChooserDropdown } from './FilterNew/DropdownFilters/FeedsChooserDropdown';
import { SourceChooserDropdown } from './FilterNew/DropdownFilters/SourceChooserDropdown';
import { GeoGroupChooserDropdown } from './FilterNew/DropdownFilters/GeoGroupChooserDropdown';
import { SourceGroupChooserDropdown } from './FilterNew/DropdownFilters/SourceGroupChooserDropdown';
import { TagsChooserDropdown } from './FilterNew/DropdownFilters/TagsChooserDropdown';
import { createNarrativeFormURL } from '../../utils/searchFiltersToFormParams';
import { MessageInfoWrapper } from '../../components/MessagesTable/MessageInfoWrapper';
import { MessageMetricsWrapper } from '../../components/MessagesTable/MessageMetricsWrapper';
import { OriginalTextBlock } from '../../components/MessagesTable/OriginalTextBlock';

const defaultDropdownFilters = ['platforms', 'contentTypes', 'languages'];

const platformMapping = {
  1: 'TELEGRAM',
  2: 'FACEBOOK',
  4: 'VK',
  3: 'TWITTER',
  5: 'WEB',
  6: 'OK',
  7: 'YOUTUBE',
  8: 'TIKTOK',
  9: 'INSTAGRAM',
  10: 'THREADS',
  11: 'LINKEDIN',
  12: 'MAX',
  13: 'ZHIHU',
  14: 'PINCONG',
  15: 'HUPU',
  16: 'WEIBO',
  17: 'DOUYIN',
  18: 'DARKWEB',
  19: 'BLUESKY',
  20: 'REDDIT',
};

const sortingFieldNames = {
  DATE: 'published_at',
  SOURCE: 'source',
  MESSAGE: 'message',
  VIEWS: 'views',
  ENGAGEMENT: 'engagement',
  RELEVANCE: 'relevance',
};

const sortingFieldNamesBack = {
  published_at: 'DATE',
  source: 'SOURCE',
  message: 'MESSAGE',
  views: 'VIEWS',
  engagement: 'ENGAGEMENT',
  relevance: 'RELEVANCE',
};

export function SearchPage() {
  const { t } = useTranslation();
  let navigate = useNavigate();
  const getUserLocale = () => {
    const lang = localStorage.getItem('i18nextLng');
    if (lang === 'ua-UK' || lang === 'ru-RU' || lang === 'ru' || lang === 'uk') {
      return uk;
    }
    return en;
  };
  const {
    state,
    dispatch,
    applyFilter,
    updateLanguages,
    setDate,
    setSimilarTo,
    setCommentsTo,
    setRepostsTo,
    setExamples,
    sortBy,
    setIsAscending,
    setCursor,
    resetFilters,
    applyDraftFilters,
    updateDraftFilters,
  } = useSearchContext();
  const [currentUser] = useCurrentUser();
  const [currentQuota] = useCurrentQuota();
  const [isQueryAssistantModalOpen, setIsQueryAssistantModalOpen] =
    useState(false);
  const [queryAssistantSession, setQueryAssistantSession] = useState(null);
  const notEnoughQuota =
    currentQuota?.billing_mode && currentQuota?.quota?.ANALYTICAL?.balance <= 0;
  const workspaceId = parseInt(localStorage.getItem('workspace_id'));
  const nativeLang = currentUser?.translation_language
    ? currentUser?.translation_language
    : window.clientInformation.language.split('-', 1)[0].toUpperCase();
  const [searchParams, setSearchParams] = useSearchParams();

  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const isAdvancedSyntax =
    workspaceConfig?.keywords_parser_class === 'Workspace16ESKeywordsQuery';
  const similarTo = searchParams.get('similar_to');
  const commentsTo = searchParams.get('comments_to');
  const repostsTo = searchParams.get('reposts_to');
  const aspectBasedSentimentAvailable = featureFlags?.includes(
    'aspect_based_sentiment',
  );
  const showSiilaritySearch = featureFlags?.includes('show_similarity_search');
  const showAIAssistant = featureFlags?.includes('neural_search');
  const showStories = featureFlags?.includes('show_stories');
  const enableDeepFakeDetector = featureFlags?.includes('deepfake_checker');

  const isAdminWorkspace = currentUser?.workspace_id === 1;

  const [dropdownMenu, setDropdownMenu] = useState(false);

  const sentimentAnalysisTitle = {
    SENTIMENT_STATEMENT: {
      title: t('Topic/Narrative'),
      hint: t(
        'Give a short name of a topic, narrative or opinion to measure reaction to. E.g.: ceasefire in Gaza, Covid vaccines can cause death, sending Western troops to Ukraine.',
      ),
    },
    SENTIMENT_PERSON: {
      title: t('Person'),
      hint: t(
        'Write a name and minimum necessary content that helps identify a person in content. E.g.: Vladimir Putin, leader of PWC Wagner',
      ),
    },
    SENTIMENT_ORGANIZATION: {
      title: t('Organization'),
      hint: t('Write a name of an organization. E.g.: UN, EU'),
    },
    SENTIMENT_COUNTRY: {
      title: t('Country'),
      hint: t('Write a name of a country. E.g.: Russia, USA'),
    },
    SENTIMENT_CUSTOM: {
      title: t('Custom sentiment option'),
      hint: t(
        'Write a custom keyword or phrase to measure sentiment towards. E.g.: "I love my country"',
      ),
    },
  };

  const aspectBasedSentimentOptions = [
    {
      label: t('Sentiment towards a topic or narrative'),
      value: 'SENTIMENT_STATEMENT',
    },
    { label: t('Sentiment towards person'), value: 'SENTIMENT_PERSON' },
    {
      label: t('Sentiment towards organization'),
      value: 'SENTIMENT_ORGANIZATION',
    },
    { label: t('Sentiment towards country'), value: 'SENTIMENT_COUNTRY' },
    { label: t('Custom sentiment option'), value: 'SENTIMENT_CUSTOM' },
  ];

  const [saveModal, setSaveModal] = useState(false);
  const [addToModal, setAddToModal] = useState(false);
  const [isQuotaChecked, setIsQuotaChecked] = useState(false);
  const [quotaModal, setQuotaModal] = useState({ isOpen: false, data: null });
  const [isQuotaChecking, setIsQuotaChecking] = useState(false);
  const [addToSourceGroupModal, setAddToSourceGroupModal] = useState(false);
  const [messages, setMessages] = useState(null);
  const [messageModal, setMessageModal] = useState({
    message: null,
    isOpen: false,
    narrartiveMessage: null,
  });
  const [activeLoadMoreButton, setActiveLoadMoreButton] = useState(false);
  const [useMultilingualSearch, setUseMultilingualSearch] = useState(
    searchParams.get('multilingual') === 'true',
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isAIPoweredSearch, setIsAIPoweredSearch] = useState(false);
  const [actorSearch, setActorSearch] = useState(
    searchParams.get('actor_search') ? searchParams.get('actor_search') : null,
  );
  const [actor, setActor] = useState(null);
  const [caseSimilarityThreshold, setCaseSimilarityThreshold] = useState(null);
  const [storiesCandidatesMinThreshold, setStoriesCandidatesMinThreshold] =
    useState(0.8);
  const [storiesGroupingMaxDistance, setStoriesGroupingMaxDistance] =
    useState(0.64);
  const [isMessageDropdownOpen, setIsMessageDropdownOpen] = useState({});
  const [openDropdownFilters, setOpenDropdownFilters] = useState({});
  const [originalPost, setOriginalPost] = useState(null);
  const pageSize = 50;
  const location = useLocation();
  const [noQuotaMessage, setNoQuotaMessage] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [isBulkTranslation, setIsBulkTranslation] = useState(false);
  const [isBulkTranslationLoading, setIsBulkTranslationLoading] =
    useState(false);

  let translationControllerRef = useRef();
  const [showSearchHint, setShowSearchHint] = useState(false);
  const isInitialMount = useRef(true);
  const filtersChangedDuringLoading = useRef(false);
  const hasPerformedSearch = useRef(false);

  // Reset filters when clicking nav link while already on Search page (ReloadNavLink behavior)
  useEffect(() => {
    if (location.state?.reload && location.state?.reload === true) {
      // Get empty filter state with proper structure
      const emptyFilters = getEmptyFilterState();

      // Reset both filters and draftFilters (new context-based approach)
      dispatch({
        type: 'APPLY_FILTER',
        payload: emptyFilters,
      });
      dispatch({
        type: 'UPDATE_DRAFT_FILTERS',
        payload: emptyFilters,
      });
      dispatch({
        type: 'SET_DATE',
        payload: { startDate: subDays(new Date(), 7), endDate: new Date() },
      });
      dispatch({ type: 'SORT_BY', fieldName: 'DATE' });
      dispatch({ type: 'IS_ASCENDING', isAscending: false });
      dispatch({ type: 'SET_CURSOR', cursor: null });

      keywordsRef.current = null;
      setIsValidQuery(true);
      setError(null);
      setValidationError(null);
      setNoQuotaMessage(null);
      setQueryAssistantSession(null);
      setShowSearchHint(false);
      isInitialMount.current = true;
      filtersChangedDuringLoading.current = false;
      hasPerformedSearch.current = false;
      setCommentsTo(null);
      setRepostsTo(null);
    }
  }, [location.state, dispatch]);

  useEffect(() => {
    if (workspaceConfig?.narratives_similarity_threshold) {
      setCaseSimilarityThreshold(
        parseFloat(workspaceConfig?.narratives_similarity_threshold),
      );
    }

    API.fetch('GET', '/API/v1/config/website_config').then((websiteConfig) => {
      if (
        !workspaceConfig?.narratives_similarity_threshold &&
        websiteConfig?.narratives_similarity_threshold
      ) {
        setCaseSimilarityThreshold(
          parseFloat(websiteConfig.narratives_similarity_threshold),
        );
      }

      if (websiteConfig?.stories_candidates_min_threshold) {
        setStoriesCandidatesMinThreshold(
          parseFloat(websiteConfig.stories_candidates_min_threshold),
        );
      }

      if (websiteConfig?.stories_grouping_max_distance) {
        setStoriesGroupingMaxDistance(
          parseFloat(websiteConfig.stories_grouping_max_distance),
        );
      }
    }).catch((error) => showRequestError(error));
  }, []);

  useEffect(() => {
    if (!actorSearch) {
      return;
    }
    API.fetch('GET', `/API/v1/sources/${actorSearch}`).then((data) => {
      setActor(data);
    }).catch((error) => showRequestError(error, t('Actor')));
  }, [actorSearch]);

  const sortingQuery = state.sorting.fieldName
    ? `${state.sorting.isAscending ? '' : '-'}${
        sortingFieldNames[state.sorting.fieldName]
      }`
    : null;

  const isAIPoweredSearchEnabled =
    !similarTo && featureFlags?.includes('ai_powered_search');
  const showMultilingualSearch = !similarTo;

  // const [startDate, setStartDate] = useState(initStartDate);
  // const [endDate, setEndDate] = useState(initEndDate);
  const decodedURLQuery =
    searchParams.get('q') == null
      ? null
      : decodeURIComponent(searchParams.get('q'));

  const queryInputRef = useRef(null);
  const keywordsRef = useRef(decodedURLQuery || null);
  const stateFiltersRef = useRef(state.filters);
  stateFiltersRef.current = state.filters;
  const [isValidQuery, setIsValidQuery] = useState(true);
  const [hasMixedNearPair, setHasMixedNearPair] = useState(false);
  const [narrativeName, setNarrativeName] = useState('');
  const [narrativeDescription, setNarrativeDescription] = useState('');
  const [narrativeCreateStories, setNarrativeCreateStories] = useState(false);
  const [
    narrativeIsAspectBasedSentimentEnabled,
    setNarrativeIsAspectBasedSentimentEnabled,
  ] = useState(false);
  const [
    narrativeAspectBasedSentimentObject,
    setNarrativeAspectBasedSentimentObject,
  ] = useState(null);
  const [
    narrativeAspectBasedSentimentSubject,
    setNarrativeAspectBasedSentimentSubject,
  ] = useState('');
  const [isCreateNarrativeDisabled, setIsCreateNarrativeDisabled] =
    useState(true);
  const [error, setError] = useState(null);
  const isValidQueryRef = useRef(isValidQuery);

  const [addToNarrativeModal, setAddToNarrativeModal] = useState(false);

  useEffect(() => {
    if ((similarTo || isAIPoweredSearch) && state.examples) {
      // Only enable if not on initial mount and user has performed a search
      if (!isInitialMount.current && hasPerformedSearch.current) {
        setIsCreateNarrativeDisabled(false);
      }
    }
  }, [state.examples, similarTo, isAIPoweredSearch]);

  useEffect(() => {
    if (keywordsRef?.current?.length > 0) {
      // Only enable if not on initial mount and user has performed a search
      if (!isInitialMount.current && hasPerformedSearch.current) {
        setIsCreateNarrativeDisabled(false);
      }
    }
  }, [keywordsRef]);

  // Re-evaluate button state when first search completes
  useEffect(() => {
    if (messages !== null && hasPerformedSearch.current) {
      const hasKeywords = keywordsRef?.current?.replace(/(\r\n|\n|\r)/gm, '').trim().length > 0;
      const hasExamples = (similarTo || isAIPoweredSearch) && state.examples;
      const hasActorGroup = state.filters.sourceGroups.value.length > 0;
      const hasActor = state.filters.sources.value.length > 0;
      const hasHighRisk = state.filters.discreditations.value.length > 0;
      const hasAffiliations = state.filters.affiliations.value.length > 0;
      const hasAffiliationTypes = state.filters.affiliationTypes.value.length > 0;

      if (!isInitialMount.current && (hasKeywords || hasExamples || hasActorGroup || hasActor || hasHighRisk || hasAffiliations || hasAffiliationTypes)) {
        setIsCreateNarrativeDisabled(false);
      } else {
        setIsCreateNarrativeDisabled(true);
      }
    }
  }, [messages, similarTo, isAIPoweredSearch, state.examples, state.filters.sourceGroups.value, state.filters.sources.value, state.filters.discreditations.value, state.filters.affiliations.value, state.filters.affiliationTypes.value]);

  const isValueSet = (value) => {
    if (Array.isArray(value) && !value.some(isValueSet)) {
      return false;
    }

    return value !== '' && value !== undefined && value !== null;
  };
  const isAnyFilterApplied = (filters) => {
    let filters_copy = { ...filters };

    const is_examples_set = isValueSet(filters_copy.examples);

    if (isAIPoweredSearch && !is_examples_set) {
      return false;
    }

    if (isValueSet(filters_copy.keywords) || is_examples_set) {
      return true;
    }

    // Drop keys don't used in filters check
    delete filters_copy.keywords;
    delete filters_copy.start_date;
    delete filters_copy.end_date;
    delete filters_copy.use_multilingual_search;

    const values = Object.values(filters_copy);

    return (
      (isValueSet(filters.start_date) || isValueSet(filters.end_date)) &&
      values.some(isValueSet)
    );
  };

  const onKeywordsChange = useCallback((value, isValid) => {
    keywordsRef.current = value;
    if (isValid !== null) {
      setIsValidQuery(isValid);
    } else if (isValid === null && !isValidQueryRef.current) {
      setIsValidQuery(true);
    }

    const k = keywordsRef.current.replace(/(\r\n|\n|\r)/gm, '');
    const hasKeywords = k.trim().length > 0;
    const hasActorGroup = stateFiltersRef.current.sourceGroups.value.length > 0;
    const hasActor = stateFiltersRef.current.sources.value.length > 0;
    const hasHighRisk = stateFiltersRef.current.discreditations.value.length > 0;
    const hasAffiliations = stateFiltersRef.current.affiliations.value.length > 0;
    const hasAffiliationTypes = stateFiltersRef.current.affiliationTypes.value.length > 0;

    // Only enable if not on initial mount and user has performed a search
    if (!isInitialMount.current && hasPerformedSearch.current && (hasKeywords || hasActorGroup || hasActor || hasHighRisk || hasAffiliations || hasAffiliationTypes)) {
      setIsCreateNarrativeDisabled(false);
    } else {
      setIsCreateNarrativeDisabled(true);
    }

    // Show hint when keywords change (after initial mount)
    if (!isInitialMount.current) {
      setShowSearchHint(true);
    }
  }, []);

  const abortControllerRef = useRef();

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (translationControllerRef.current) {
        translationControllerRef.current.abort();
      }
    };
  }, []);

  const createURL = (filtersOverride = null) => {
    const urlParams = new URLSearchParams();
    const filters = filtersOverride || state.draftFilters;

    const keyword = keywordsRef.current
      ? keywordsRef.current.replace(/(\r\n|\n|\r)/gm, ' ')
      : '';

    if (state.parentCompositeId) {
      if (state.isRepostsMode) {
        urlParams.set('reposts_to', state.parentCompositeId);
      } else {
        urlParams.set('comments_to', state.parentCompositeId);
      }
    }

    if (keyword && keyword?.trim().length > 0) {
      urlParams.set('q', encodeURIComponent(keyword));
    }

    if (state.startDate) {
      urlParams.set('start_date', format(state.startDate, 'yyyy-LL-dd'));
    }

    if (state.endDate) {
      urlParams.set('end_date', format(state.endDate, 'yyyy-LL-dd'));
    }

    if (filters.platforms.value.length > 0) {
      filters.platforms.value.forEach((platform) => {
        urlParams.append('platforms', platform);
      });
    }

    if (filters.sentiment.value.length > 0) {
      filters.sentiment.value.forEach((sentiment) => {
        urlParams.append('sentiment', sentiment);
      });
    }

    if (filters.sourceGroups.value.length > 0) {
      filters.sourceGroups.value.forEach((sourceGroup) => {
        urlParams.append('source_group_ids', sourceGroup);
      });

      if (filters.sourceGroups.exclude === true) {
        urlParams.set('groups_exclude', 'true');
      }
    }

    if (filters.feeds.value.length > 0) {
      filters.feeds.value.forEach((feed) => {
        urlParams.append('feeds', feed);
      });

      if (filters.feeds.exclude === true) {
        urlParams.set('feeds_exclude', 'true');
      }
    }

    if (filters.tags.value.length > 0) {
      filters.tags.value.forEach((tag) => {
        urlParams.append('tags', tag);
      });

      if (filters.tags.exclude === true) {
        urlParams.set('tags_exclude', 'true');
      }
    }

    if (filters.geoGroup.value.length > 0) {
      filters.geoGroup.value.forEach((geoGroup) => {
        urlParams.append('geo_group', geoGroup);
      });
      if (filters.geoGroup.exclude === true) {
        urlParams.set('geo_group_exclude', 'true');
      }
    }

    if (filters.languages.value.length > 0) {
      filters.languages.value.forEach((language) => {
        urlParams.append('languages', language);
      });

      if (filters.languages.exclude === true) {
        urlParams.set('languages_exclude', 'true');
      }
    }

    if (filters.contentTypes.value.length > 0) {
      filters.contentTypes.value.forEach((contentType) => {
        urlParams.append('content_types', contentType);
      });
    }

    if (filters.sources.value.length > 0) {
      filters.sources.value.forEach((source) => {
        urlParams.append('source_ids', source);
      });
      if (filters.sources.exclude === true) {
        urlParams.set('sources_exclude', 'true');
      }
    }

    if (filters.bots.value === true) {
      urlParams.set('show_bots', 'true');
    }

    if (filters.bots.exclude === true) {
      urlParams.set('bots_exclude', 'true');
    }

    if (filters.discreditations.value.length > 0) {
      filters.discreditations.value.forEach((discreditation) => {
        urlParams.append('IoC', discreditation);
      });

      if (filters.discreditations.exclude === true) {
        urlParams.set('discreditations_exclude', 'true');
      }
    }

    if (filters.countries.value.length > 0) {
      filters.countries.value.forEach((country) => {
        urlParams.append('source_origin_country_ids', country);
      });

      if (filters.countries.exclude === true) {
        urlParams.set('countries_exclude', 'true');
      }
    }

    if (
      filters.affiliations.value.length > 0 &&
      filters.affiliations.value[0] === 'all'
    ) {
      urlParams.set('affiliations', 'all');
      if (filters.affiliations.exclude === true) {
        urlParams.set('affiliations_exclude', 'true');
      }
    } else if (filters.affiliations.value.length > 0) {
      filters.affiliations.value.forEach((affiliation) => {
        urlParams.append('affiliations', affiliation);
      });

      if (filters.affiliations.exclude === true) {
        urlParams.set('affiliations_exclude', 'true');
      }
    }

    if (filters.affiliationTypes.value.length > 0) {
      filters.affiliationTypes.value.forEach((type) => {
        urlParams.append('affiliation_types', type);
      });

      if (filters.affiliationTypes.exclude === true) {
        urlParams.set('affiliation_types_exclude', 'true');
      }
    }

    if (state.sorting) {
      if (Array.isArray(state.sorting.fieldName)) {
        state.sorting.fieldName.forEach((element) => {
          urlParams.append(
            'sorting',
            `${state.sorting.isAscending ? '' : '-'}${element}`,
          );
        });
      } else {
        urlParams.set('sorting', sortingQuery);
      }
    }

    if (similarTo) {
      urlParams.set('similar_to', similarTo);
    }

    if (useMultilingualSearch) {
      urlParams.set('multilingual', 'true');
    }

    return urlParams.toString();
  };

  const fetchMessages = useCallback(
    (
      keywords,
      start,
      end,
      platforms,
      sourceGroups,
      isValid,
      languages,
      sources,
      countries,
      sorting,
      sentiment,
      contentTypes,
      bots,
      discreditations,
      affiliations,
      feeds,
      useMultilingualSearch,
      isAIPoweredSearch,
      countriesExclude,
      languagesExclude,
      sourceGroupsExclude,
      sourcesExclude,
      affiliationCountriesExclude,
      feedsExclude,
      geoGroup,
      geoGroupExclude,
      botsExclude,
      discreditationsExclude = false,
      tags = [],
      tagsExclude = false,
      affiliationTypes = [],
      affiliationTypesExclude = false,
      examples = null,
      cursor = null,
      parentCompositeId = null,
    ) => {
      const urlParams = new URLSearchParams();
      urlParams.set('size', pageSize);

      if (cursor) {
        urlParams.set('cursor', state.cursor);
      } else {
        setMessages(null);
      }

      cancelTranslateAll();
      setIsBulkTranslationLoading(false);
      setIsBulkTranslation(false);

      const keyword =
        keywords?.trim().length > 0
          ? keywords.replace(/(\r\n|\n|\r)/gm, ' ')
          : '';

      const bodyRequest = {
        start_date: start ? format(start, 'yyyy-LL-dd') : null,
        end_date: end ? format(end, 'yyyy-LL-dd') : null,
        use_multilingual_search: useMultilingualSearch,
      };

      let excludingFilters = [];

      if (parentCompositeId) {
        bodyRequest.parent_composite_ids = [parentCompositeId];
      }

      if (isAIPoweredSearch) {
        bodyRequest.examples = [keyword];
      } else {
        bodyRequest.keywords = [keyword];
      }

      if (platforms) {
        bodyRequest.platforms = platforms;
      }

      if (sentiment) {
        bodyRequest.sentiment = sentiment;
      }

      if (examples) {
        bodyRequest.examples = examples;
      }

      if (sourceGroups) {
        bodyRequest.source_group_ids = sourceGroups;
        if (sourceGroupsExclude) {
          excludingFilters.push('source_group_ids');
        }
      }

      if (feeds) {
        bodyRequest.feed_ids = feeds;

        if (feedsExclude) {
          excludingFilters.push('feed_ids');
        }
      }

      if (tags && tags.length > 0) {
        bodyRequest.tags = tags;

        if (tagsExclude) {
          excludingFilters.push('tags');
        }
      }

      if (geoGroup) {
        bodyRequest.country_group_ids = geoGroup;
        if (geoGroupExclude) {
          excludingFilters.push('country_group_ids');
        }
      }

      if (languages) {
        let enumList = languages;
        bodyRequest.languages = enumList;
        if (languagesExclude) {
          excludingFilters.push('languages');
        }
      }

      if (state.isRepostsMode) {
        bodyRequest.content_types = ['REPOST'];
      } else if (parentCompositeId && !state.isRepostsMode) {
        bodyRequest.content_types = ['COMMENT'];
      } else if (contentTypes) {
        bodyRequest.content_types = contentTypes;
      }

      if (sources) {
        bodyRequest.source_ids = sources;
        if (sourcesExclude) {
          excludingFilters.push('source_ids');
        }
      }

      if (countries) {
        bodyRequest.source_origin_country_ids = countries;
        if (countriesExclude) {
          excludingFilters.push('source_origin_country_ids');
        }
      }

      if (bots === true) {
        bodyRequest.source_bots = true;
      }

      if (botsExclude === true) {
        excludingFilters.push('source_bots');
      }

      if (discreditations) {
        bodyRequest.source_discreditated_entity_types = discreditations;
        if (discreditationsExclude === true) {
          excludingFilters.push('source_discreditated_entity_types');
        }
      }

      if (affiliations.length > 0 && affiliations[0] === 'all') {
        bodyRequest.source_state_affiliated_all_countries = true;
        if (affiliationCountriesExclude) {
          excludingFilters.push('source_state_affiliated_all_countries');
        }
      } else {
        bodyRequest.source_state_affiliated_country_ids = affiliations;
        if (affiliationCountriesExclude) {
          excludingFilters.push('source_state_affiliated_country_ids');
        }
      }

      if (affiliationTypes && affiliationTypes.length > 0) {
        bodyRequest.source_affiliation_types = toApiAffiliationTypes(affiliationTypes);
        if (affiliationTypesExclude) {
          excludingFilters.push('source_affiliation_types');
        }
      }

      if (excludingFilters.length > 0) {
        bodyRequest.exclusive_filters = excludingFilters;
      }

      if (!isAnyFilterApplied(bodyRequest) && !parentCompositeId) {
        return;
      }

      if (sorting) {
        if (Array.isArray(sorting.fieldName)) {
          sorting.fieldName.forEach((element) => {
            urlParams.append(
              'sorting',
              `${sorting.isAscending ? '' : '-'}${element}`,
            );
          });
        } else {
          urlParams.set('sorting', sortingQuery);
        }
      }

      setIsLoading(true);

      // Check if there's an existing request and abort it
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create a new AbortController
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const signal = abortController.signal;

      setError(null);
      setValidationError(null);

      let executionTimeHeader = null;
      const requestStartTime = performance.now();

      API.fetch(
        'POST',
        `/API/v1/messages/search?${urlParams.toString()}`,
        null,
        bodyRequest,
        signal,
        undefined,
        (response) => {
          executionTimeHeader = response.headers.get('x-execution-time');
        },
      )
        .then((data) => {
          if (currentUser?.is_super_admin) {
            try {
              const browserDuration = ((performance.now() - requestStartTime) / 1000).toFixed(3);
              const timings = executionTimeHeader ? JSON.parse(executionTimeHeader) : {};
              const formatLabel = (key) =>
                key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
              const formatValue = (ns) => (ns / 1_000_000_000).toFixed(3);

              toast.info(
                <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                  <strong style={{ fontSize: '14px' }}>Search Execution Time</strong>
                  <div style={{ marginTop: '6px' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '12px',
                        borderBottom: '1px solid rgba(255,255,255,0.15)',
                        paddingBottom: '4px',
                        marginBottom: '4px',
                      }}
                    >
                      <span style={{ color: '#ccc' }}>Browser Request</span>
                      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                        {browserDuration}s
                      </span>
                    </div>
                    {Object.entries(timings).map(([key, value]) => (
                      <div
                        key={key}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '12px',
                        }}
                      >
                        <span style={{ color: '#ccc' }}>{formatLabel(key)}</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                          {formatValue(value)}s
                        </span>
                      </div>
                    ))}
                  </div>
                </div>,
                { autoClose: 5000 },
              );
            } catch (e) {
              // ignore parse errors
            }
          }

          setIsLoading(false);
          setShowSearchHint(false);
          hasPerformedSearch.current = true;

          // If no cursor - we haven't fetched messages before, so just set them
          // otherwise append new messages to the end
          if (!cursor) {
            if (isBulkTranslation) {
              setIsBulkTranslation(false);
            }
            setMessages(data);
          } else {
            setMessages((prevMessages) => {
              return {
                ...data,
                objects: [...prevMessages?.objects, ...data?.objects],
              };
            });
          }

          dispatch({ type: 'SET_CURSOR', cursor: data.cursor });

          ampli.track({
            event_type: 'Search Perform',
            event_properties: {
              found_results: data.total,
              search_history_id: data.search_history_id,
              keywords_filter: !!keywords,
              platforms_filter: platforms && platforms.length > 0,
              source_groups_filter: sourceGroups && sourceGroups.length > 0,
              feeds_filter: feeds && feeds.length > 0,
              country_groups_filter: geoGroup && geoGroup.length > 0,
              languages_filter: languages && languages.length > 0,
              content_types_filter: contentTypes && contentTypes.length > 0,
              sources_filter: sources && sources.length > 0,
              origin_countries_filter: countries && countries.length > 0,
              bots_filter: bots === true || botsExclude === true,
              discreditations_filter:
                discreditations && discreditations.length > 0,
              state_affiliation_filter: affiliations && affiliations.length > 0,
            },
          });
        })
        .catch((e) => {
          if (e.name === 'AbortError') {
            return;
          }

          dispatch({ type: 'SET_CURSOR', cursor: null });
          setActiveLoadMoreButton(false);

          if (e.cause && e.cause === 402 && messages !== null) {
            setNoQuotaMessage(
              'Your workspace has reached its Analytical quota limit. Contact your manager to request more quota or wait till the next billing month.',
            );
          } else if (e.cause && e.cause === 402 && messages === null) {
            setError(
              'Your workspace has reached its Analytical quota limit. Contact your manager to request more quota or wait till the next billing month.',
            );
          } else if (e.cause && e.cause?.status === 412) {
            const error_message_raw = e?.message
              .replaceAll('{', '{{')
              .replaceAll('}', '}}');
            const error_parameters = e?.cause?.parameters;
            const error_message_translated = t(
              error_message_raw,
              error_parameters,
            )
              .replaceAll('&amp;', '&')
              .replaceAll('&lt;', '<')
              .replaceAll('&gt;', '>')
              .replaceAll('&quot;', '"')
              .replaceAll('&apos;', "'")
              .replaceAll('&#39;', "'");
            setValidationError(error_message_translated);
            setError(error_message_translated);
          } else if (
            e?.message &&
            (e.message.toLowerCase().includes('too complex') ||
              e.message.toLowerCase().includes('wildcard'))
          ) {
            setError(
              t(
                'Too many wildcard (*) characters in query. Please optimize and retry.',
              ),
            );
          } else {
            showRequestError(e, t('Search'));
            const errorMessage = e.name === 'TimeoutError'
              ? t('Search request timed out. Please try again.')
              : isAdminWorkspace && e.message ? e.message : t('Something went wrong...');
            setError(errorMessage);
          }
          setIsLoading(false);
        });
    },
    [state.cursor, sortingQuery, state.sorting, state.isRepostsMode],
  );

  useEffect(() => {
    if (state.cursor !== null && isBulkTranslation) {
      handleBulkTranslateClick();
    }
  }, [state.cursor]);

  useEffect(() => {
    if (isValidQueryRef?.current) {
      navigate(`?${createURL()}`);
    }
    fetchMessages(
      keywordsRef.current,
      state.startDate,
      state.endDate,
      state.filters.platforms.value,
      state.filters.sourceGroups.value,
      isValidQuery,
      state.filters.languages.value,
      state.filters.sources.value,
      state.filters.countries.value,
      state.sorting,
      state.filters.sentiment.value,
      state.filters.contentTypes.value,
      state.filters.bots.value,
      state.filters.discreditations.value,
      state.filters.affiliations.value,
      state.filters.feeds.value,
      useMultilingualSearch,
      isAIPoweredSearch,
      state.filters.countries.exclude,
      state.filters.languages.exclude,
      state.filters.sourceGroups.exclude,
      state.filters.sources.exclude,
      state.filters.affiliations.exclude,
      state.filters.feeds.exclude,
      state.filters.geoGroup.value,
      state.filters.geoGroup.exclude,
      state.filters.bots.exclude,
      state.filters.discreditations.exclude,
      state.filters.tags.value,
      state.filters.tags.exclude,
      state.filters.affiliationTypes.value,
      state.filters.affiliationTypes.exclude,
      state.examples,
      state.cursor,
      state.parentCompositeId,
    );
  }, [
    // state.startDate,
    // state.endDate,
    // state.filters.platforms.value,
    // state.filters.sourceGroups.value,
    state.sorting,
    // state.filters.languages.value,
    // state.filters.sources.value,
    // state.filters.countries.value,
    // state.filters.sentiment.value,
    // state.filters.contentTypes.value,
    // state.filters.bots.value,
    // state.filters.discreditations.value,
    // state.filters.affiliations.value,
    // state.filters.feeds.value,
    // state.filters.geoGroup.value,
    // state.filters.geoGroup.exclude,
    // state.examples,
    // state.filters.countries.exclude,
    // state.filters.languages.exclude,
    // state.filters.sourceGroups.exclude,
    // state.filters.sources.exclude,
    // state.filters.affiliations.exclude,
    // state.filters.feeds.exclude,
    // state.filters.bots.exclude,
    // state.parentCompositeId,
  ]);

  useEffect(() => {
    ampli.track({ event_type: 'Search Page Open' });
  }, []);

  useEffect(() => {
    setActiveLoadMoreButton(
      messages &&
        messages.cursor !== null &&
        ((messages.total !== null &&
          messages.objects.length < messages.total) ||
          messages.total === null),
    );
  }, [messages]);

  const [searchQuery, setSearchQuery] = useState(null);
  const [narratives, setNarratives] = useState(null);
  const [selectedNarrative, setSelectedNarrative] = useState(null);
  const [isSaveSearchQuery, setIsSaveSearchQuery] = useState(true);

  const fetchNarratives = useCallback(
    (searchQuery) => {
      const urlParams = new URLSearchParams();
      urlParams.set('size', '10');
      // urlParams.set('page', state.page);

      if (searchQuery !== null && searchQuery.length > 0) {
        urlParams.set('q', searchQuery);
      }

      API.fetch('GET', `/API/v1/narratives?${urlParams.toString()}`).then(
        (data) => {
          setNarratives(data);
        },
      ).catch((error) => showRequestError(error, t('Cases')));
    },
    [searchQuery],
  );

  useEffect(() => {
    fetchNarratives(searchQuery);
  }, [searchQuery]);

  const getAllComments = useCallback((parent_composite_id) => {
    if (!parent_composite_id) {
      return;
    }

    API.fetch('GET', `/API/v1/messages/composite_id/${parent_composite_id}`)
      .then((data) => {
        setOriginalPost(data.text);
        dispatch({
          type: 'SET_COMMENTS_TO',
          payload: { parentCompositeId: parent_composite_id },
        });
        setIsValidQuery(true);
      })
      .catch((e) => {
        setError(e.message);
      });
  }, []);

  useEffect(() => {
    if (commentsTo) {
      getAllComments(commentsTo);
    }
  }, [commentsTo, getAllComments]);

  const getAllReposts = useCallback((parent_composite_id) => {
    if (!parent_composite_id) {
      return;
    }

    API.fetch('GET', `/API/v1/messages/composite_id/${parent_composite_id}`)
      .then((data) => {
        setOriginalPost(data.text);
        dispatch({
          type: 'SET_REPOSTS_TO',
          payload: { parentCompositeId: parent_composite_id },
        });
        setIsValidQuery(true);
      })
      .catch((e) => {
        setError(e.message);
      });
  }, []);

  useEffect(() => {
    if (repostsTo) {
      getAllReposts(repostsTo);
    }
  }, [repostsTo, getAllReposts]);

  const findSimilarTo = useCallback((composite_id) => {
    if (!composite_id) {
      return;
    }

    setIsLoading(true);
    API.fetch('GET', `/API/v1/messages/composite_id/${composite_id}`)
      .then((data) => {
        let date_publicated = new Date(data.date_publicated);
        let start_date = subDays(date_publicated, 7);
        let end_date = addDays(date_publicated, 7);
        // setExamples([data.text]);

        dispatch({
          type: 'SET_SIMILAR_TO',
          payload: {
            startDate: start_date,
            endDate: end_date,
            examples: [data.text],
          },
        });
      })
      .catch((e) => {
        setError(e.message);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    findSimilarTo(similarTo);
  }, [findSimilarTo, similarTo]);

  const handleCreateNarrativ = (
    name,
    description,
    createStories,
    isAspectBasedSentimentEnabled,
    aspectBasedSentimentObject,
    aspectBasedSentimentSubject,
    keywords,
    startDate,
    endDate,
    platforms,
    sourceGroups,
    languages,
    sources,
    countries,
    sentiment,
    contentTypes,
    bots,
    discreditations,
    affiliations,
    feeds,
    examples,
    useMultilingualSearch,
    isAIPoweredSearch,
    isSimilarTo,
    languagesExclude,
    countriesExclude,
    sourceGroupsExclude,
    sourcesExclude,
    affiliationCountriesExclude,
    feedsExclude,
    geoGroup,
    geoGroupExclude,
    botsExclude,
    discreditationsExclude,
    affiliationTypes = [],
    affiliationTypesExclude = false,
  ) => {
    setIsQuotaChecking(true);
    let keyword;
    if (keywords) {
      keyword =
        keywords?.trim().length > 0
          ? keywords.replace(/(\r\n|\n|\r)/gm, ' ')
          : '';
    }

    let allAffiliationsCountries = false;

    if (affiliations.length > 0 && affiliations[0] === 'all') {
      allAffiliationsCountries = true;
    }

    let excludedFilters = [];

    if (languagesExclude) {
      excludedFilters.push('languages');
    }

    if (countriesExclude) {
      excludedFilters.push('source_origin_country_ids');
    }

    if (sourceGroupsExclude) {
      excludedFilters.push('source_group_ids');
    }

    if (feedsExclude) {
      excludedFilters.push('feed_ids');
    }

    if (geoGroupExclude) {
      excludedFilters.push('country_group_ids');
    }

    if (sourcesExclude) {
      excludedFilters.push('source_ids');
    }

    if (affiliationCountriesExclude) {
      excludedFilters.push('source_state_affiliated_country_ids');
    }

    if (botsExclude) {
      excludedFilters.push('source_bots');
    }

    if (discreditationsExclude) {
      excludedFilters.push('source_discreditated_entity_types');
    }

    if (affiliationTypesExclude && affiliationTypes.length > 0) {
      excludedFilters.push('source_affiliation_types');
    }

    API.fetch('POST', `/API/v1/narratives`, null, {
      name: name,
      description: description ? description : '',
      is_active: true,
      type: 'NARRATIVE',
      parameters: {
        keywords: isAIPoweredSearch || isSimilarTo ? [] : [keyword],
        examples: examples ? examples : isAIPoweredSearch ? [keyword] : [],
        start_date: startDate ? format(startDate, 'yyyy-LL-dd') : null,
        end_date: endDate ? format(endDate, 'yyyy-LL-dd') : null,
        source_types: platforms
          ? platforms?.map((platform) => platformMapping[platform])
          : [],
        source_group_ids: sourceGroups ? sourceGroups : [],
        languages: languages
          ? languages.map((lang) => languageNumberMapping[lang])
          : [],
        source_ids: sources ? sources : [],
        source_origin_country_ids: countries ? countries : [],
        sentiment: sentiment ? sentiment : [],
        content_types: contentTypes ? contentTypes : [],
        source_bots: bots,
        source_discreditated_entity_types: discreditations
          ? discreditations
          : [],
        source_state_affiliated_all_countries: allAffiliationsCountries,
        source_state_affiliated_country_ids: allAffiliationsCountries
          ? []
          : affiliations,
        source_affiliation_types: toApiAffiliationTypes(affiliationTypes),
        feed_ids: feeds ? feeds : [],
        country_group_ids: geoGroup ? geoGroup : [],
        translate_keywords_query: useMultilingualSearch,
        enable_stories_summarization: createStories ? true : false,
        stories_summarization_language: createStories ? (currentUser?.translation_language || "EN") : null,
        create_stories: createStories,
        stories_candidates_min_threshold: createStories
          ? storiesCandidatesMinThreshold
          : null,
        stories_grouping_max_distance: createStories
          ? storiesGroupingMaxDistance
          : null,
        aspect_based_sentiment: isAspectBasedSentimentEnabled,
        aspect_based_sentiment_object: isAspectBasedSentimentEnabled
          ? aspectBasedSentimentObject?.value
          : null,
        aspect_based_sentiment_subject: isAspectBasedSentimentEnabled
          ? aspectBasedSentimentSubject
          : null,
        exclusive_filters: excludedFilters?.length > 0 ? excludedFilters : null,
        apply_supervised_classifier: false,
        keywords_query_origin_language: '',
        collect_comments_to_posts: false,
        include_comments_to_stories: false,
        topic_modeling_translate_language: null,
      },
      adjustment_suggestions: null,
      confirm_quota_usage: isQuotaChecked,
    }).then((data) => {
      if (!isQuotaChecked) {
        setIsQuotaChecking(false);
        setIsQuotaChecked(true);
        if (data?.id) {
          ampli.track({
            event_type: 'Search Saved As Narrative',
            event_properties: {
              narrative_id: data?.id,
              workspace_id: currentUser?.workspace_id,
              is_stories_applied: createStories,
              is_aspect_based_sentiment_applied: isAspectBasedSentimentEnabled,
            },
          });
          setSaveModal(false);
          setIsQuotaChecked(false);
          setIsQuotaChecking(false);
          setNarrativeName('');
          setNarrativeDescription('');
          setNarrativeCreateStories(false);
          setNarrativeIsAspectBasedSentimentEnabled(false);
          setNarrativeAspectBasedSentimentObject(null);
          setNarrativeAspectBasedSentimentSubject('');
          toast.success(t('Case created'));
          window.open(`/narratives/${data?.id}`, '_blank');
        } else {
          setSaveModal(false);
          setQuotaModal({ isOpen: true, data: data });
        }
      } else {
        setSaveModal(false);
        setIsQuotaChecked(false);
        setIsQuotaChecking(false);
        setNarrativeName('');
        setNarrativeDescription('');
        setNarrativeCreateStories(false);
        setNarrativeIsAspectBasedSentimentEnabled(false);
        setNarrativeAspectBasedSentimentObject(null);
        setNarrativeAspectBasedSentimentSubject('');
        ampli.track({
          event_type: 'Search Saved As Narrative',
          event_properties: {
            narrative_id: data?.id,
            workspace_id: currentUser?.workspace_id,
            is_stories_applied: createStories,
            is_aspect_based_sentiment_applied: isAspectBasedSentimentEnabled,
          },
        });
        toast.success(t('Case created'));
        window.open(`/narratives/${data?.id}`, '_blank');
      }
    }).catch((error) => {
      showRequestError(error, t('Create case'));
      setIsQuotaChecking(false);
    });
  };

  if (currentUser?.workspace?.config?.is_reporter_only_user) {
    // redirect UCBI reporters to a Report an instance
    navigate('/threats/new');
  }

  const handleAddToNarrativ = () => {};

  const handleBulkTranslation = async (
    messagesToTranslate,
    abortController,
  ) => {
    const BATCH_SIZE = 5;
    const nativeLanguage = currentUser?.translation_language
      ? currentUser?.translation_language
      : window.clientInformation.language.split('-', 1)[0];

    const batches = [];
    for (let i = 0; i < messagesToTranslate.length; i += BATCH_SIZE) {
      batches.push(messagesToTranslate.slice(i, i + BATCH_SIZE));
    }

    for (const batch of batches) {
      if (abortController?.current.aborted) {
        break;
      }
      try {
        await processBatch(batch, nativeLanguage, abortController);
      } catch (error) {
        if (error.name === 'AbortError') {
          break;
        }
        showRequestError(error);
        break;
      }
    }
  };

  const processBatch = async (batch, nativeLanguage, abortController) => {
    if (!messages || !messages.objects) {
      return;
    }

    if (abortController?.current?.aborted) {
      return;
    }
    const translationPromises = batch.map((message) => {
      const originalPostText =
        message.content_type === 'COMMENT' ? replaceNewlinesWithBreaks(message.parent_message?.highlighted_text || message.parent_message?.text) : '';
      const textToTranslate = message.highlighted_text ? replaceNewlinesWithBreaks(message.highlighted_text) : replaceNewlinesWithBreaks(message.message);

      if (originalPostText) {
        return Promise.allSettled([
          translateMessage(
            message.message_url,
            originalPostText,
            nativeLanguage,
            null,
            abortController,
          ),
          translateMessage(
            message.message_url,
            textToTranslate,
            nativeLanguage,
            null,
            abortController,
          ),
        ]).then((results) => ({
          messageURL: message.message_url,
          type: 'comment',
          parentResult: results[0],
          messageResult: results[1],
        }));
      } else {
        return translateMessage(
          message.message_url,
          textToTranslate,
          nativeLanguage,
          null,
          abortController,
        )
          .then((data) => ({
            messageURL: message.message_url,
            type: 'message',
            messageResult: { status: 'fulfilled', value: data },
          }))
          .catch((error) => ({
            messageURL: message.message_url,
            type: 'message',
            messageResult: { status: 'rejected', reason: error },
          }));
      }
    });

    const batchResults = await Promise.allSettled(translationPromises);

    if (abortController?.current?.aborted) {
      return;
    }

    if (!messages || !messages?.objects) {
      return;
    }

    setMessages((prevMessages) => {
      let newMessagesList = Array.isArray(prevMessages?.objects)
        ? [...prevMessages.objects]
        : [];

      batchResults.forEach((result) => {
        if (result.status === 'fulfilled') {
          const { messageURL, type, parentResult, messageResult } =
            result.value;

          newMessagesList = newMessagesList?.map((message) => {
            if (message?.message_url === messageURL) {
              let updatedMessage = {
                ...message,
                isTranslation: true,
              };

              if (type === 'comment' && parentResult) {
                if (parentResult.status === 'fulfilled') {
                  updatedMessage.parent_message = {
                    ...message?.parent_message,
                    isTranslation: true,
                    translated: parentResult.value.destination_text,
                  };
                } else {
                  updatedMessage.parent_message = {
                    ...message?.parent_message,
                    isTranslation: true,
                    translated: t('We were not able to translate this text.'),
                    error: true,
                  };
                }
              }

              if (messageResult.status === 'fulfilled') {
                updatedMessage.translated =
                  messageResult.value.destination_text;
              } else {
                updatedMessage.translated = t(
                  'We were not able to translate this text.',
                );
                updatedMessage.error = true;
              }

              return updatedMessage;
            }
            return message;
          });
        }
      });

      return { ...prevMessages, objects: newMessagesList };
    });
  };

  const toggleAllToTranslation = (messageURLs) => {
    setMessages((prevMessages) => ({
      ...prevMessages,
      objects: prevMessages.objects.map((message) => {
        if (messageURLs.includes(message.message_url)) {
          return {
            ...message,
            isTranslation: true,
            ...(message.content_type === 'COMMENT' &&
              message.parent_message && {
                parent_message: {
                  ...message.parent_message,
                  isTranslation: true,
                },
              }),
          };
        }
        return message;
      }),
    }));
  };

  const toggleAllToOriginal = (messageURLs) => {
    setMessages((prevMessages) => ({
      ...prevMessages,
      objects: prevMessages.objects.map((message) => {
        if (messageURLs.includes(message.message_url)) {
          return {
            ...message,
            isTranslation: false,
            ...(message.content_type === 'COMMENT' &&
              message.parent_message && {
                parent_message: {
                  ...message.parent_message,
                  isTranslation: false,
                },
              }),
          };
        }
        return message;
      }),
    }));
  };

  const handleBulkTranslateClick = async () => {
    setIsBulkTranslationLoading(true);

    const messagesToTranslate = [];
    const alreadyTranslated = [];

    if (translationControllerRef.current) {
      translationControllerRef.current.abort();
    }

    translationControllerRef.current = new AbortController();

    messages.objects.forEach((message) => {
      if (message.translated) {
        alreadyTranslated.push(message);
      } else {
        messagesToTranslate.push(message);
      }
    });

    if (messagesToTranslate.length === 0) {
      console.log('No messages to translate');
      const messageURLs = alreadyTranslated.map((msg) => msg.message_url);
      toggleAllToTranslation(messageURLs);
      setIsBulkTranslationLoading(false);
      return;
    }

    if (alreadyTranslated.length > 0) {
      toggleAllToTranslation(alreadyTranslated.map((msg) => msg.message_url));
    }

    const messageURLs = messagesToTranslate.map((msg) => msg.message_url);
    toggleAllToTranslation(messageURLs);

    try {
      await handleBulkTranslation(
        messagesToTranslate,
        translationControllerRef,
      );
      setIsBulkTranslationLoading(false);
    } catch (error) {
      setIsBulkTranslationLoading(false);
      showRequestError(error);
    } finally {
      translationControllerRef.current = null;
    }
  };

  const handleShowOriginalClick = () => {
    cancelTranslateAll();
    const translatedMessages = messages.objects.filter(
      (message) => message.translated,
    );
    const messageURLs = translatedMessages.map((msg) => msg.message_url);

    toggleAllToOriginal(messageURLs);
    setIsBulkTranslationLoading(false);
  };

  const cancelTranslateAll = () => {
    console.log('Cancelling bulk translation');
    if (translationControllerRef.current) {
      translationControllerRef.current.abort();
    }
  };

  const translateMessage = (
    messageURL,
    text,
    nativeLang,
    sourceLang = '',
    abortController,
  ) => {
    let body;
    if (sourceLang) {
      body = {
        text: text,
        destination_language: nativeLang,
        source_language: sourceLang,
      };
    } else {
      body = {
        text: text,
        destination_language: nativeLang,
      };
    }
    return API.fetch(
      'POST',
      '/API/v1/translations/translate',
      null,
      body,
      abortController?.current?.signal,
    );
  };

  const toggleTranslate = (messageURL, isTranslation) => {
    setMessages({
      ...messages,
      objects: messages.objects.map((message) => {
        if (message.message_url === messageURL) {
          message.isTranslation = isTranslation;
          if (message.content_type === 'COMMENT' && message.parent_message) {
            message.parent_message.isTranslation = isTranslation;
          }
          return message;
        } else {
          return message;
        }
      }),
    });
  };

  const handleTranslation = (messageURL, text, isTranslation) => {
    const target = messages.objects.find(
      (message) => message.message_url === messageURL,
    );
    const originalPostText =
      target?.content_type === 'COMMENT' ? replaceNewlinesWithBreaks(target?.parent_message?.highlighted_text || target?.parent_message?.text) : '';
    if (target.translated) {
      toggleTranslate(messageURL, isTranslation);
    } else {
      toggleTranslate(messageURL, true);
      const nativeLanguage = currentUser?.translation_language
        ? currentUser?.translation_language
        : window.clientInformation.language.split('-', 1)[0];

      let newMessagesList = [...messages.objects];

      if (originalPostText) {
        Promise.allSettled([
          translateMessage(messageURL, originalPostText, nativeLanguage),
          translateMessage(messageURL, text, nativeLanguage),
        ]).then((results) => {
          if (results[0].status === 'fulfilled') {
            const originalPostData = results[0].value;

            newMessagesList = newMessagesList.map((message) => {
              if (message.message_url === messageURL) {
                let m = {
                  ...message,
                  parent_message: {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: originalPostData.destination_text,
                  },
                };
                return m;
              } else {
                return message;
              }
            });
          } else {
            newMessagesList = newMessagesList.map((message) => {
              if (message.message_url === messageURL) {
                return {
                  ...message,
                  parent_message: {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: t('We were not able to translate this text.'),
                    error: true,
                  },
                };
              } else {
                return message;
              }
            });
          }

          if (results[1].status === 'fulfilled') {
            const messageData = results[1].value;
            newMessagesList = newMessagesList.map((message) => {
              if (message.message_url === messageURL) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: messageData.destination_text,
                };
              } else {
                return message;
              }
            });
          } else {
            newMessagesList = newMessagesList.map((message) => {
              if (message.message_url === messageURL) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: t('We were not able to translate this text.'),
                  error: true,
                };
              } else {
                return message;
              }
            });
          }

          setMessages({ ...messages, objects: newMessagesList });
        });
      } else {
        translateMessage(messageURL, text, nativeLanguage)
          .then((data) => {
            newMessagesList = newMessagesList.map((message) => {
              if (message.message_url === messageURL) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: data.destination_text,
                };
              } else {
                return message;
              }
            });

            setMessages({ ...messages, objects: newMessagesList });
          })
          .catch((e) => {
            newMessagesList = newMessagesList.map((message) => {
              if (message.message_url === messageURL) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: t('We were not able to translate this text.'),
                  error: true,
                };
              } else {
                return message;
              }
            });

            setMessages({ ...messages, objects: newMessagesList });
          });
      }
    }
  };

  const clearActorBannerIfRemoved = () => {
    if (actorSearch && !state.draftFilters.sources.value.includes(Number(actorSearch))) {
      setActorSearch(null);
      setActor(null);
    }
  };

  const buttonsOnScroll = () => {
    if (Object.keys(selectedMessages).length > 0) {
      const buttons = document.getElementById('bulk');
      let scrolly = window.pageYOffset;
      if (!buttons) {
        return;
      }

      if (scrolly > 150) {
        buttons.classList.add(styles.floating);
      } else {
        buttons.classList.remove(styles.floating);
      }
    } else {
      return;
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', buttonsOnScroll);
    window.addEventListener('mousemove', buttonsOnScroll);

    return () => {
      window.removeEventListener('scroll', buttonsOnScroll);
      window.removeEventListener('mousemove', buttonsOnScroll);
    };
  });

  let noResults;
  let guideLink;

  if (
    localStorage.getItem('i18nextLng') === 'ua-UK' ||
    localStorage.getItem('i18nextLng') === 'ru-RU' ||
    localStorage.getItem('i18nextLng') === 'ru' ||
    localStorage.getItem('i18nextLng') === 'uk'
  ) {
    guideLink =
      'https://maple-fuel-09c.notion.site/C-796a2dfb19ca40a1a03551df662d0bac';
  } else {
    guideLink =
      'https://docs.osavul.cloud/search-page';
  }

  const noResultsText = (
    <>
      {t('Enter your query above. For help with search syntax, see our ')}{' '}
      <a href={guideLink} target="_blank" rel="noreferrer">
        {t('search guide')}
      </a>
      .
    </>
  );

  if (messages === null) {
    // noResults = t('We are not able to show results, add your request');
    noResults = noResultsText;
  } else if (messages?.objects.length === 0) {
    noResults = t(
      'We could not find any matches in the Osavul Data Warehouse. Please refine your search.',
    );
  }

  const sortingMenu = [
    {
      name: t('Date'),
      value: 'DATE',
    },
    {
      name: t('Relevance'),
      value: 'RELEVANCE',
    },
    {
      name: t('Views'),
      value: 'VIEWS',
    },
    {
      name: t('Engagement'),
      value: 'ENGAGEMENT',
    },
  ];

  const sortingOrder = [
    {
      name: t('A-Z'),
      icon: <ArrowIcon />,
      value: true,
    },
    {
      name: t('Z-A'),
      value: false,
      icon: <ArrowDownIcon />,
    },
  ];

  const [highlights, setHighlights] = useState(['KEYWORDS']);
  const [modalHighlights, setModalHighlights] = useState([]);
  const [highlightsMenu, setHighlightsMenu] = useState(false);

  const [selectedMessages, setSelectedMessages] = useState({});

  const toggleSelected = (messageId) => {
    if (messageId in selectedMessages) {
      let newMessagesList = Object.fromEntries(
        Object.entries(selectedMessages).filter(
          ([k, v]) => k !== messageId.toString(),
        ),
      );
      setSelectedMessages(newMessagesList);
    } else {
      setSelectedMessages({ ...selectedMessages, [messageId]: true });
    }
  };

  const toggleAll = () => {
    if (areAllIdsSelected(messages.objects, selectedMessages)) {
      const messagesToRemove = { ...messages };
      const updatedSelectedMessages = { ...selectedMessages };

      for (const message of messagesToRemove.objects) {
        const messageId = message.id;
        if (updatedSelectedMessages.hasOwnProperty(messageId)) {
          delete updatedSelectedMessages[messageId];
        }
      }
      setSelectedMessages(updatedSelectedMessages);
    } else {
      const newSelectedMessages = Object.fromEntries(
        messages.objects.map((message) => [message.id, true]),
      );
      setSelectedMessages({ ...selectedMessages, ...newSelectedMessages });
    }
  };

  const clearAllSelectedMessages = () => {
    setSelectedMessages({});
  };

  const getQuotaModalComponent = () => {
    if (!quotaModal?.isOpen || !currentQuota?.billing_mode) return null;

    const commonProps = {
      isOpen: quotaModal.isOpen,
      isTopicModeling: false,
      onClose: () => {
        setQuotaModal({ isOpen: false, data: '' });
        setIsQuotaChecked(false);
        setNarrativeName('');
        setNarrativeDescription('');
        setNarrativeCreateStories(false);
        setNarrativeIsAspectBasedSentimentEnabled(false);
        setNarrativeAspectBasedSentimentObject(null);
        setNarrativeAspectBasedSentimentSubject('');
      },
      data: quotaModal.data,
      buttonText: t('Create case'),
      onChange: (e) => {
        e.preventDefault();
        setQuotaModal({ isOpen: false, data: '' });

        handleCreateNarrativ(
          narrativeName,
          narrativeDescription,
          narrativeCreateStories,
          narrativeIsAspectBasedSentimentEnabled,
          narrativeAspectBasedSentimentObject,
          narrativeAspectBasedSentimentSubject,
          keywordsRef.current,
          state.startDate,
          state.endDate,
          state.filters.platforms.value,
          state.filters.sourceGroups.value,
          state.filters.languages.value,
          state.filters.sources.value,
          state.filters.countries.value,
          state.filters.sentiment.value,
          state.filters.contentTypes.value,
          state.filters.bots.value,
          state.filters.discreditations.value,
          state.filters.affiliations.value,
          state.filters.feeds.value,
          state.examples,
          useMultilingualSearch,
          isAIPoweredSearch,
          similarTo,
          state.filters.languages.exclude,
          state.filters.countries.exclude,
          state.filters.sourceGroups.exclude,
          state.filters.sources.exclude,
          state.filters.affiliations.exclude,
          state.filters.feeds.exclude,
          state.filters.geoGroup.value,
          state.filters.geoGroup.exclude,
          state.filters.bots.exclude,
          state.filters.discreditations.exclude,
          state.filters.affiliationTypes.value,
          state.filters.affiliationTypes.exclude,
        );
      },
      redirect: false,
    };

    switch (currentQuota.billing_mode) {
      case 'v0.2':
        return <QuotaCheckModal {...commonProps} />;
      case 'v0.1':
        return <QuotaCheckModalOld {...commonProps} />;
      default:
        return null;
    }
  };

  const createCaseButtonTooltip = () => {
    if (notEnoughQuota) {
      return t('No quota left');
    } else if (isCreateNarrativeDisabled && hasPerformedSearch?.current === true) {
      return t('Add keywords1');
    } else if (isCreateNarrativeDisabled && hasPerformedSearch?.current === false) {
      return t('Run a search first');
    } else if (isValidQuery === false) {
      return t('Invalid search query');
    } else {
      return t('Save as case');
    }
  };

  const handleDropdownOpenChange = (filterName, isOpen) => {
    setOpenDropdownFilters(prev => ({
      ...prev,
      [filterName]: isOpen
    }));
  };

  const getMapDropdownFilters = () => ({
    countries: <CountriesChooserDropdown onOpenChange={(isOpen) => handleDropdownOpenChange('countries', isOpen)} />,
    platforms: <PlatformChooserDropdown onOpenChange={(isOpen) => handleDropdownOpenChange('platforms', isOpen)} />,
    contentTypes: <ContentTypesChooserDropdown onOpenChange={(isOpen) => handleDropdownOpenChange('contentTypes', isOpen)} />,
    affiliations: <AffiliationCountriesDropdown onOpenChange={(isOpen) => handleDropdownOpenChange('affiliations', isOpen)} />,
    affiliationTypes: <AffiliationTypesChooserDropdown onOpenChange={(isOpen) => handleDropdownOpenChange('affiliationTypes', isOpen)} />,
    bots: <BotsChooserDropdown onOpenChange={(isOpen) => handleDropdownOpenChange('bots', isOpen)} />,
    discreditations: <IoCChooserDropdown onOpenChange={(isOpen) => handleDropdownOpenChange('discreditations', isOpen)} />,
    languages: <LanguageChooserDropdown onOpenChange={(isOpen) => handleDropdownOpenChange('languages', isOpen)} />,
    feeds: <FeedsChooserDropdown onOpenChange={(isOpen) => handleDropdownOpenChange('feeds', isOpen)} />,
    sources: <SourceChooserDropdown onOpenChange={(isOpen) => handleDropdownOpenChange('sources', isOpen)} />,
    sourceGroups: <SourceGroupChooserDropdown onOpenChange={(isOpen) => handleDropdownOpenChange('sourceGroups', isOpen)} />,
    geoGroup: <GeoGroupChooserDropdown onOpenChange={(isOpen) => handleDropdownOpenChange('geoGroup', isOpen)} />,
    tags: <TagsChooserDropdown onOpenChange={(isOpen) => handleDropdownOpenChange('tags', isOpen)} />,
  });

  const filtersToShow = () => {
    const mapDropdownFilters = getMapDropdownFilters();
    const activeFilters = [];
    Object.entries(state.draftFilters).forEach(([filterName, filter]) => {
      if (mapDropdownFilters[filterName]) {
        const hasValue =
          filterName === 'bots'
            ? filter.value === true
            : Array.isArray(filter.value) && filter.value.length > 0;

        const isOpen = openDropdownFilters[filterName];
        const shouldShow = hasValue || isOpen;

        if (shouldShow) {
          activeFilters.push(filterName);
        }
      }
    });

    const filtersToRender = [
      ...new Set([...defaultDropdownFilters, ...activeFilters]),
    ];

    return filtersToRender.map((filterName) => (
      <Fragment key={filterName}>{mapDropdownFilters[filterName]}</Fragment>
    ));
  };

  useEffect(() => {
    // Don't show hint on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // If search is in progress, track that filters changed
    if (isLoading) {
      filtersChangedDuringLoading.current = true;
      return;
    }

    // Show hint on any filter or date change
    setShowSearchHint(true);
  }, [
    state.draftFilters.affiliations,
    state.draftFilters.feeds,
    state.draftFilters.bots,
    state.draftFilters.contentTypes,
    state.draftFilters.countries,
    state.draftFilters.languages,
    state.draftFilters.platforms,
    state.draftFilters.sourceGroups,
    state.draftFilters.sources,
    state.draftFilters.sentiment,
    state.draftFilters.discreditations,
    state.draftFilters.affiliationTypes,
    state.draftFilters.geoGroup,
    state.draftFilters.tags,
    state.startDate,
    state.endDate,
    useMultilingualSearch,
  ]);

  // Hide hint when search starts loading, show it when loading completes if filters changed
  useEffect(() => {
    if (isLoading) {
      setShowSearchHint(false);
    } else if (filtersChangedDuringLoading.current) {
      // Loading completed and filters changed during loading - show hint
      setShowSearchHint(true);
      filtersChangedDuringLoading.current = false;
    }
  }, [isLoading]);


  return (
    <div className={styles.root}>
      <div className="page-header">
        <div className="breadcrumb"></div>
        <div className="controls">
          {/* <button
            className={`${styles.button} ${styles.save}`}
            onClick={() => {
              setAddToModal(true)
              fetchNarratives()
            }}
            disabled={isCreateNarrativeDisabled}
          >
            <PlusIcon />
            {t('Add to narrative')}
          </button> */}
          <Tooltip content={createCaseButtonTooltip()} position="bottom">
            <button
              className={styles.saveAsCase}
              onClick={() => {
                // Update current search URL with applied filters before navigating
                // This ensures browser back returns to the correct search state
                const currentSearchUrl = createURL(state.filters);
                window.history.replaceState(null, '', `?${currentSearchUrl}`);

                const formUrl = createNarrativeFormURL({
                  filters: state.filters,
                  keywords: keywordsRef.current,
                  startDate: state.startDate,
                  endDate: state.endDate,
                  isMultilingual: useMultilingualSearch,
                });

                const targetUrl = `/narratives/new?${formUrl}`;
                const ua = navigator.userAgent;
                const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
                const isFirefoxIOS = /FxiOS/i.test(ua);
                const shouldNavigateInSameTab = isSafari || isFirefoxIOS;

                if (shouldNavigateInSameTab) {
                  navigate(targetUrl);
                } else {
                  window.open(targetUrl, '_blank', 'noopener,noreferrer');
                }

                ampli.track({
                  event_type: 'Search Save As Narrative Click',
                });
              }}
              disabled={
                notEnoughQuota ||
                isCreateNarrativeDisabled ||
                state.parentCompositeId ||
                isValidQuery === false
              }
              data-tooltip-id="create-case"
              data-tooltip-content={
                currentQuota?.billing_mode &&
                currentQuota?.quota?.ANALYTICAL?.balance > 0
                  ? t('Create case')
                  : t('No quota left')
              }
            >
              <SaveIcon />
              {t('Save as case')}
            </button>
          </Tooltip>
        </div>
      </div>
      <div className="next-card-header">
        <div className="row">
          <h2>{''}</h2>
          {Object.keys(selectedMessages).length > 0 ? (
            <div className={styles.bulkButtonsWrapper} id="bulk">
              <button>
                {Object.keys(selectedMessages).length} {t('selected')}{' '}
                <span
                  className={styles.clear}
                  onClick={() => clearAllSelectedMessages()}
                >
                  <CloseIcon />
                </span>
              </button>
              <button onClick={() => setAddToNarrativeModal(true)}>
                <AddToIcon />
                {t('Add to case')}
              </button>

              <button onClick={() => setAddToSourceGroupModal(true)}>
                <AddToIcon />
                {t('Add to actor group')}
              </button>
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
      <div className={styles.searchPanelWrapper}>
        <div className={styles.searchCheckboxesWrapper}>
          {showAIAssistant ? (
            <button
              className={`'button-new' ${styles.queryAssistantButton}`}
              onClick={() => {
                setIsQueryAssistantModalOpen(true);
              }}
            >
              <AIQueryIcon />
              {t('Query Assistant - Beta')}
            </button>
          ) : (
            ''
          )}
          {isAIPoweredSearchEnabled ? (
            <div className={styles.aipoweredSearchCheckboxWrapper}>
              <SwitchSmall
                value={isAIPoweredSearch}
                id={'checkbox-ai-powered-search'}
                onChange={() => {
                  setIsAIPoweredSearch(!isAIPoweredSearch);
                  dispatch({ type: 'SET_CURSOR', cursor: null });
                }}
              />
              <label
                htmlFor="checkbox-ai-powered-search"
                className={styles.aipoweredSearchLabel}
              >
                {t('AI-powered search')}
              </label>
              <Tooltip content={t('AI-powered search hint')} position="top">
                <AnchorIcon />
              </Tooltip>
            </div>
          ) : (
            ''
          )}
        </div>
        <div className={styles.searchWrapper}>
          <QueryInput
            onMixedNearPairChange={setHasMixedNearPair}
            keywords={keywordsRef.current}
            isAdvanced={isAdvancedSyntax}
            search={true}
            clear={() => {
              setMessages(null);
              cancelTranslateAll();
              setIsBulkTranslationLoading(false);
              setIsBulkTranslation(false);
              setSearchQuery(null);
              keywordsRef.current = '';
            }}
            ref={queryInputRef}
            className={styles.queryInput}
            onChange={onKeywordsChange}
            isInvalid={!isValidQuery}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && event.shiftKey === false) {
                event.preventDefault();
                // Apply draft filters before search
                applyDraftFilters();
                clearActorBannerIfRemoved();
                if (isValidQueryRef?.current) {
                  navigate(`?${createURL()}`);
                } else if (!isValidQueryRef?.current && isAIPoweredSearch) {
                  setIsValidQuery(true);
                  navigate(`?${createURL()}`);
                }
                dispatch({ type: 'SET_CURSOR', cursor: null });
                fetchMessages(
                  keywordsRef.current,
                  state.startDate,
                  state.endDate,
                  state.draftFilters.platforms.value,
                  state.draftFilters.sourceGroups.value,
                  isValidQuery,
                  state.draftFilters.languages.value,
                  state.draftFilters.sources.value,
                  state.draftFilters.countries.value,
                  state.sorting,
                  state.draftFilters.sentiment.value,
                  state.draftFilters.contentTypes.value,
                  state.draftFilters.bots.value,
                  state.draftFilters.discreditations.value,
                  state.draftFilters.affiliations.value,
                  state.draftFilters.feeds.value,
                  useMultilingualSearch,
                  isAIPoweredSearch,
                  state.draftFilters.countries.exclude,
                  state.draftFilters.languages.exclude,
                  state.draftFilters.sourceGroups.exclude,
                  state.draftFilters.sources.exclude,
                  state.draftFilters.affiliations.exclude,
                  state.draftFilters.feeds.exclude,
                  state.draftFilters.geoGroup.value,
                  state.draftFilters.geoGroup.exclude,
                  state.draftFilters.bots.exclude,
                  state.draftFilters.discreditations.exclude,
                  state.draftFilters.tags.value,
                  state.draftFilters.tags.exclude,
                  state.draftFilters.affiliationTypes.value,
                  state.draftFilters.affiliationTypes.exclude,
                  state.examples,
                );
              }
            }}
            onClick={(e) => {
              e.preventDefault();
              // Apply draft filters before search
              applyDraftFilters();
              clearActorBannerIfRemoved();
              if (isValidQueryRef?.current) {
                navigate(`?${createURL()}`);
              } else if (!isValidQueryRef?.current && isAIPoweredSearch) {
                setIsValidQuery(true);
                navigate(`?${createURL()}`);
              }
              dispatch({ type: 'SET_CURSOR', cursor: null });
              fetchMessages(
                keywordsRef.current,
                state.startDate,
                state.endDate,
                state.draftFilters.platforms.value,
                state.draftFilters.sourceGroups.value,
                isValidQuery,
                state.draftFilters.languages.value,
                state.draftFilters.sources.value,
                state.draftFilters.countries.value,
                state.sorting,
                state.draftFilters.sentiment.value,
                state.draftFilters.contentTypes.value,
                state.draftFilters.bots.value,
                state.draftFilters.discreditations.value,
                state.draftFilters.affiliations.value,
                state.draftFilters.feeds.value,
                useMultilingualSearch,
                isAIPoweredSearch,
                state.draftFilters.countries.exclude,
                state.draftFilters.languages.exclude,
                state.draftFilters.sourceGroups.exclude,
                state.draftFilters.sources.exclude,
                state.draftFilters.affiliations.exclude,
                state.draftFilters.feeds.exclude,
                state.draftFilters.geoGroup.value,
                state.draftFilters.geoGroup.exclude,
                state.draftFilters.bots.exclude,
                state.draftFilters.discreditations.exclude,
                state.draftFilters.tags.value,
                state.draftFilters.tags.exclude,
                state.draftFilters.affiliationTypes.value,
                state.draftFilters.affiliationTypes.exclude,
                state.examples,
              );
            }}
          />

          {showMultilingualSearch ? (
            <div
              className={styles.multilingualSearchCheckboxWrapper}
              onClick={() => {
                setUseMultilingualSearch(!useMultilingualSearch);
                dispatch({ type: 'SET_CURSOR', cursor: null });
              }}
              data-tooltip-id="multilingual-tooltip"
              data-tooltip-content={t('Multilingual search text')}
            >
              <span className={styles.switchWrapper}>
                <SwitchSmall
                  value={useMultilingualSearch}
                  id={'checkbox-multilingual-search'}
                />
              </span>
              <span>
                {t('Multilingual search')}
              </span>
              <ReactTooltip id="multilingual-tooltip" style={{zIndex: '10000'}}/>
            </div>
          ) : (
            ''
          )}

          <div className={styles.calendarWrapper}>
            <DatePickerWrapper
              onChange={(startDate, endDate) => {
                dispatch({
                  type: 'SET_DATE',
                  payload: { startDate: startDate, endDate: endDate },
                });
              }}
              initStartDate={state.startDate}
              initEndDate={state.endDate}
              isRange={true}
              buttonDateFormat="dd/MM/yy"
            />
          </div>
        </div>

        <div className={styles.filtersWrapper}>
          <div className={styles.filtersContainer}>
            <SearchPageFilter />
            {filtersToShow()}
          </div>
          <div className={styles.searchButtonWrapper}>
            {showSearchHint ? <div className={styles.searchHint}>
              {t('Search to update results')}
            </div> : null}
            <button
              className={styles.searchButton}
              onClick={(e) => {
                e.preventDefault();
                applyDraftFilters();
                clearActorBannerIfRemoved();
                if (isValidQueryRef?.current) {
                  navigate(`?${createURL()}`);
                } else if (!isValidQueryRef?.current && isAIPoweredSearch) {
                  setIsValidQuery(true);
                  navigate(`?${createURL()}`);
                }
                dispatch({ type: 'SET_CURSOR', cursor: null });

                fetchMessages(
                  keywordsRef.current,
                  state.startDate,
                  state.endDate,
                  state.draftFilters.platforms.value,
                  state.draftFilters.sourceGroups.value,
                  isValidQuery,
                  state.draftFilters.languages.value,
                  state.draftFilters.sources.value,
                  state.draftFilters.countries.value,
                  state.sorting,
                  state.draftFilters.sentiment.value,
                  state.draftFilters.contentTypes.value,
                  state.draftFilters.bots.value,
                  state.draftFilters.discreditations.value,
                  state.draftFilters.affiliations.value,
                  state.draftFilters.feeds.value,
                  useMultilingualSearch,
                  isAIPoweredSearch,
                  state.draftFilters.countries.exclude,
                  state.draftFilters.languages.exclude,
                  state.draftFilters.sourceGroups.exclude,
                  state.draftFilters.sources.exclude,
                  state.draftFilters.affiliations.exclude,
                  state.draftFilters.feeds.exclude,
                  state.draftFilters.geoGroup.value,
                  state.draftFilters.geoGroup.exclude,
                  state.draftFilters.bots.exclude,
                  state.draftFilters.discreditations.exclude,
                  state.draftFilters.tags.value,
                  state.draftFilters.tags.exclude,
                  state.draftFilters.affiliationTypes.value,
                  state.draftFilters.affiliationTypes.exclude,
                  state.examples,
                );
              }}
            >
              {t('Search')}
            </button>
          </div>
        </div>
      </div>

      {hasMixedNearPair && !isAIPoweredSearch ? (
        <NearMixedPairWarning
          className={styles.nearMixedPairWarning}
          startDate={state.startDate}
          isAdvanced={isAdvancedSyntax}
        />
      ) : null}

      {commentsTo ? (
        <div className={styles.similarMessagesSearchWrapper}>
          <div className={styles.infoIcon}> </div>
          <div className={styles.messageText}>
            <div className={styles.title}>
              {t(`Showing all comments to the post`)}:
            </div>
            <ExpandableText
              length={150}
              text={originalPost || ''}
              textClassName={styles.text}
            />
            <CloseIcon
              className={styles.closeIcon}
              onClick={() => {
                dispatch({
                  type: 'SET_COMMENTS_TO',
                  payload: { parentCompositeId: null },
                });
                searchParams.delete('comments_to');
                setSearchParams(searchParams);
              }}
            />
          </div>
        </div>
      ) : (
        ''
      )}

      {repostsTo ? (
        <div className={styles.similarMessagesSearchWrapper}>
          <div className={styles.infoIcon}> </div>
          <div className={styles.messageText}>
            <div className={styles.title}>
              {t(`Showing all reposts to the post`)}:
            </div>
            <ExpandableText
              length={150}
              text={originalPost || ''}
              textClassName={styles.text}
            />
            <CloseIcon
              className={styles.closeIcon}
              role="button"
              tabIndex={0}
              aria-label={t('Clear reposts filter')}
              onClick={() => {
                dispatch({
                  type: 'SET_REPOSTS_TO',
                  payload: { parentCompositeId: null },
                });
                searchParams.delete('reposts_to');
                setSearchParams(searchParams);
                fetchMessages(
                  keywordsRef.current,
                  state.startDate,
                  state.endDate,
                  state.filters.platforms.value,
                  state.filters.sourceGroups.value,
                  isValidQuery,
                  state.filters.languages.value,
                  state.filters.sources.value,
                  state.filters.countries.value,
                  state.sorting,
                  state.filters.sentiment.value,
                  state.filters.contentTypes.value,
                  state.filters.bots.value,
                  state.filters.discreditations.value,
                  state.filters.affiliations.value,
                  state.filters.feeds.value,
                  useMultilingualSearch,
                  isAIPoweredSearch,
                  state.filters.countries.exclude,
                  state.filters.languages.exclude,
                  state.filters.sourceGroups.exclude,
                  state.filters.sources.exclude,
                  state.filters.affiliations.exclude,
                  state.filters.feeds.exclude,
                  state.filters.geoGroup.value,
                  state.filters.geoGroup.exclude,
                  state.filters.bots.exclude,
                  state.filters.discreditations.exclude,
                  state.filters.tags.value,
                  state.filters.tags.exclude,
                  state.filters.affiliationTypes.value,
                  state.filters.affiliationTypes.exclude,
                  state.examples,
                  null,
                  null,
                );
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  dispatch({
                    type: 'SET_REPOSTS_TO',
                    payload: { parentCompositeId: null },
                  });
                  searchParams.delete('reposts_to');
                  setSearchParams(searchParams);
                  fetchMessages(
                    keywordsRef.current,
                    state.startDate,
                    state.endDate,
                    state.filters.platforms.value,
                    state.filters.sourceGroups.value,
                    isValidQuery,
                    state.filters.languages.value,
                    state.filters.sources.value,
                    state.filters.countries.value,
                    state.sorting,
                    state.filters.sentiment.value,
                    state.filters.contentTypes.value,
                    state.filters.bots.value,
                    state.filters.discreditations.value,
                    state.filters.affiliations.value,
                    state.filters.feeds.value,
                    useMultilingualSearch,
                    isAIPoweredSearch,
                    state.filters.countries.exclude,
                    state.filters.languages.exclude,
                    state.filters.sourceGroups.exclude,
                    state.filters.sources.exclude,
                    state.filters.affiliations.exclude,
                    state.filters.feeds.exclude,
                    state.filters.geoGroup.value,
                    state.filters.geoGroup.exclude,
                    state.filters.bots.exclude,
                    state.filters.discreditations.exclude,
                    state.filters.tags.value,
                    state.filters.tags.exclude,
                    state.filters.affiliationTypes.value,
                    state.filters.affiliationTypes.exclude,
                    state.examples,
                    null,
                    null,
                  );
                }
              }}
            />
          </div>
        </div>
      ) : (
        ''
      )}

      {similarTo && state.examples && state.examples.length > 0 ? (
        <div className={styles.similarMessagesSearchWrapper}>
          <div className={styles.infoIcon}> </div>
          <div className={styles.messageText}>
            <div className={styles.title}>
              {t(`Showing results for Similar publication search`)}:
            </div>
            <ExpandableText
              length={150}
              text={state.examples[0]}
              textClassName={styles.text}
            />
            <CloseIcon
              className={styles.closeIcon}
              onClick={() => {
                dispatch({ type: 'SET_EXAMPLES', payload: { examples: null } });
                searchParams.delete('similar_to');
                setSearchParams(searchParams);
              }}
            />
          </div>
        </div>
      ) : (
        ''
      )}

      {actorSearch && actor ? (
        <div className={styles.similarMessagesSearchWrapper}>
          <div className={styles.infoIcon}> </div>
          <div className={styles.messageText}>
            <div className={styles.title}>
              {t('Showing search results for the actor:')}
              <span className={styles.messageActorName}> "{actor.name}"</span>
              {state.startDate && state.endDate && (
                <span className={styles.actorDateRange}>
                  {' '}{t('for')} {format(state.startDate, 'd MMM yyyy', { locale: getUserLocale() })} – {format(state.endDate, 'd MMM yyyy', { locale: getUserLocale() })}
                </span>
              )}
            </div>
            <CloseIcon
              className={styles.closeIcon}
              onClick={() => {
                setActorSearch(null);
                setActor(null);
              }}
            />
          </div>
        </div>
      ) : (
        ''
      )}

      {isLoading && !activeLoadMoreButton ? (
        <Loader />
      ) : messages?.objects.length > 0 && !error ? (
        <table
          className={`${messageStyles.extendedMessages} ${styles.extendedMessages} search-page-messages`}
        >
          <thead>
            <tr>
              <td>
                <CheckboxField
                  onChange={() => toggleAll(messages, selectedMessages)}
                  checked={
                    // Object.keys(selectedMessages).length === messages.objects.length
                    areAllIdsSelected(messages.objects, selectedMessages)
                  }
                />
              </td>
              <td className={messageStyles.headerInfo}>
                <span>
                  {t('Total publications') +
                    ': ' +
                    formatNumber(
                      messages.total !== null
                        ? messages.total
                        : messages.objects.length,
                    )}
                  {(similarTo || isAIPoweredSearch) && messages.total >= 1000
                    ? '+'
                    : ''}
                </span>
              </td>
              <td>
                <div
                  className={messageStyles.sortingWrapper}
                  style={{ position: 'absolute' }}
                >
                  <DropdownMenu
                    isOpen={dropdownMenu}
                    onClick={() => setDropdownMenu(!dropdownMenu)}
                    menuClassName="searchSorting"
                    buttonName={`${t('Sort by')}: ${t(
                      sortingMenu.find(
                        (item) => item.value === state.sorting.fieldName,
                      )?.name || '',
                    )}`}
                    icon={<ChevronIcon />}
                    frontIcon={<SortIcon />}
                  >
                    <Radiobutton
                      itemsList={sortingMenu}
                      current={state.sorting.fieldName}
                      onChange={(value) => {
                        dispatch({ type: 'SORT_BY', fieldName: value });
                        setDropdownMenu(false);
                      }}
                    />

                    <SortingOrder
                      itemsList={sortingOrder}
                      onClick={(value) =>
                        dispatch({ type: 'IS_ASCENDING', isAscending: value })
                      }
                      current={state.sorting.isAscending}
                    />
                  </DropdownMenu>
                </div>
                <div className={messageStyles.translationSwitchWrapper}>
                  <TranslationSwitch
                    isBulkTranslation={true}
                    isTranslation={isBulkTranslation}
                    isLoading={isBulkTranslationLoading}
                    theme="dark"
                    size="medium"
                    handleTranslation={(bool) => {
                      if (bool) {
                        setIsBulkTranslation(bool);
                        handleBulkTranslateClick();
                        ampli.track({
                          event_type: 'Bulk Translation Click',
                          event_properties: {
                            workspace_id: currentUser?.workspace_id,
                            user_id: currentUser?.id,
                            event_location: 'Search Page',
                          },
                        });
                      } else {
                        setIsBulkTranslation(false);
                        handleShowOriginalClick();
                      }
                    }}
                  />
                </div>
              </td>
            </tr>
          </thead>
          <tbody>
            {messages.objects.map((message, index) => {
              let followers_cnt = message?.source?.audience;

              let textClass = messageStyles.extendedMessage;
              let textClassParent = messageStyles.extendedMessage;

              if (
                message.parent_message?.error &&
                message.parent_message?.isTranslation
              ) {
                textClassParent = `${messageStyles.extendedMessage} ${messageStyles.error}`;
              } else if (message.error && !message.isTranslation) {
                textClassParent = messageStyles.extendedMessage;
              }

              if (message.error && message.isTranslation) {
                textClass = `${messageStyles.extendedMessage} ${messageStyles.error}`;
              } else if (message.error && !message.isTranslation) {
                textClass = messageStyles.extendedMessage;
              }

              const textForTranslation = message.highlighted_text
                ? replaceNewlinesWithBreaks(message.highlighted_text)
                : replaceNewlinesWithBreaks(message.message);

              let messageMenuOptions = [];
              if (showSiilaritySearch) {
                messageMenuOptions.push({
                  name: t('Find similar publications'),
                  value: 'similar',
                });
              }

              if (
                (message?.content_type === 'COMMENT' &&
                  (message?.parent_message || message?.parent_id)) ||
                message?.content_type === 'POST'
              ) {
                messageMenuOptions.push({
                  name: t('Find comments to post'),
                  value: 'comments',
                });
              }

              if (
                message?.content_type === 'VIDEO' &&
                message?.source?.source_type === 'TIKTOK'
              ) {
                messageMenuOptions.push({
                  name: t('Find comments to video'),
                  value: 'comments_to_video',
                });
              }

              if (
                message?.content_type === 'POST' &&
                ['TIKTOK', 'FACEBOOK', 'TELEGRAM'].includes(message?.source?.source_type)
              ) {
                messageMenuOptions.push({
                  name: t('Find reposts to post'),
                  value: 'reposts_to_post',
                });
              }

              return (
                <tr key={`message.message_url_${index}`}>
                  <td>
                    <CheckboxField
                      checked={message.id in selectedMessages}
                      onChange={() => toggleSelected(message.id)}
                    />
                  </td>
                  <td>
                    <MessageInfoWrapper
                      message={message}
                      followers_cnt={followers_cnt}
                      isShare={false}
                      currentUser={currentUser}
                      narrartiveMessage={null}
                      setMessageModal={setMessageModal}
                    />

                    <div
                      className={`${messageStyles.messageContainer} ${styles.messageContainer}`}
                    >
                      <MessageMetricsWrapper
                        message={message}
                        isMessageDropdownOpen={isMessageDropdownOpen}
                        setIsMessageDropdownOpen={setIsMessageDropdownOpen}
                        messageMenuOptions={messageMenuOptions}
                      />
              
                      {message.parent_message ? (
                        <OriginalTextBlock
                          message={message}
                          currentUser={currentUser}
                          textClassParent={textClassParent}
                          enableDeepFakeDetector={enableDeepFakeDetector}
                        />
                      ) : (
                        ''
                      )}

                      {message?.images && message?.images?.length > 0 ? (
                        <ImagesContainer
                          images={message?.images}
                          showCount={3}
                        />
                      ) : (
                        ''
                      )}
                      <div>
                        {message.isTranslation === true &&
                        !message.translated ? (
                          <div>
                            <LoaderSmall />
                          </div>
                        ) : (
                          <ExpandableText
                            length={150}
                            highlights={highlights}
                            text={
                              message.isTranslation && message.translated
                                ? message.translated
                                : message.highlighted_text || message.message
                            }
                            textClassName={textClass}
                          />
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    {isAIPoweredSearch || similarTo ? (
                      <div className={messageStyles.controls}>
                        <Feedback
                          context={
                            isAIPoweredSearch
                              ? feedbackContexts.AI_POWERED_SEARCH_MESSAGE
                              : feedbackContexts.SIMILARITY_MESSAGE
                          }
                          feedbackParams={{
                            selected_message_composite_id: message.id,
                            selected_message_similarity: message.similarity,
                            search_id: messages?.search_history_id,
                            ...(isAIPoweredSearch
                              ? { query: keywordsRef?.current }
                              : { search_message_composite_id: similarTo }),
                          }}
                        />
                      </div>
                    ) : (
                      ''
                    )}

                    <div className={styles.translationWrapper}>
                      <TranslationSwitch
                        isTranslation={message.isTranslation}
                        theme="light"
                        size="medium"
                        handleTranslation={(bool) => {
                          handleTranslation(
                            message.message_url,
                            textForTranslation,
                            bool,
                          );
                        }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : error ? (
        <div className={styles.error}>
          {error && validationError ? validationError : t(error)}
        </div>
      ) : (
        <div className={styles.noResults}>{noResults}</div>
      )}
      <MessageTooltips scope=".search-page-messages" />

      {activeLoadMoreButton &&
      messages?.objects?.length > 0 &&
      !noQuotaMessage ? (
        <button
          className={styles.loadMore}
          disabled={isLoading || isBulkTranslationLoading}
          onClick={(e) => {
            e.preventDefault();
            fetchMessages(
              keywordsRef.current,
              state.startDate,
              state.endDate,
              state.filters.platforms.value,
              state.filters.sourceGroups.value,
              isValidQuery,
              state.filters.languages.value,
              state.filters.sources.value,
              state.filters.countries.value,
              state.sorting,
              state.filters.sentiment.value,
              state.filters.contentTypes.value,
              state.filters.bots.value,
              state.filters.discreditations.value,
              state.filters.affiliations.value,
              state.filters.feeds.value,
              useMultilingualSearch,
              isAIPoweredSearch,
              state.filters.countries.exclude,
              state.filters.languages.exclude,
              state.filters.sourceGroups.exclude,
              state.filters.sources.exclude,
              state.filters.affiliations.exclude,
              state.filters.feeds.exclude,
              state.filters.geoGroup.value,
              state.filters.geoGroup.exclude,
              state.filters.bots.exclude,
              state.filters.discreditations.exclude,
              state.filters.tags.value,
              state.filters.tags.exclude,
              state.filters.affiliationTypes.value,
              state.filters.affiliationTypes.exclude,
              state.examples,
              state.cursor,
              state.parentCompositeId,
            );
            ampli.track({
              event_type: 'Search Page clicked',
              event_properties: { load_more: true },
            });
          }}
        >
          {isLoading ? <LoaderSmall /> : t('Load more')}
        </button>
      ) : noQuotaMessage ? (
        <div className={styles.error}>{t(noQuotaMessage)}</div>
      ) : (
        ''
      )}

      <Modal
        isVisible={saveModal}
        title={t('Save as case')}
        doNotCloseOnOutsideClick={true}
        content={
          <div className={styles.modalForm} style={{ width: '520px' }}>
            <form
              method="post"
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateNarrativ(
                  narrativeName,
                  narrativeDescription,
                  narrativeCreateStories,
                  narrativeIsAspectBasedSentimentEnabled,
                  narrativeAspectBasedSentimentObject,
                  narrativeAspectBasedSentimentSubject,
                  keywordsRef.current,
                  state.startDate,
                  state.endDate,
                  state.platforms,
                  state.sourceGroups,
                  state.languages,
                  state.sources,
                  state.countries,
                  state.sentiment,
                  state.contentTypes,
                  state.bots,
                  state.discreditations,
                  state.affiliations,
                  state.feeds,
                  state.examples,
                  useMultilingualSearch,
                  isAIPoweredSearch,
                  similarTo,
                  state.languagesExclude,
                  state.countriesExclude,
                  state.sourceGroupsExclude,
                  state.sourcesExclude,
                  state.affiliationCountriesExclude,
                  state.feedsExclude,
                  state.geoGroup,
                  state.geoGroupExclude,
                  state.botsExclude,
                );
              }}
            >
              <div className="form-element">
                <label htmlFor="modal-name">{t('Name')}</label>
                <input
                  type="text"
                  id="modal-name"
                  onChange={(e) => setNarrativeName(e.target.value)}
                />
              </div>
              <div className="form-element">
                <label htmlFor="modal-description">{t('Description')}</label>
                <textarea
                  id="modal-description"
                  onChange={(e) => setNarrativeDescription(e.target.value)}
                ></textarea>
              </div>
              {showStories ? (
                <div className="form-element">
                  <label htmlFor="create-stories" className={styles.label}>
                    <span className={styles.labelIcon}>
                      <AIIcon />
                    </span>
                    {t('Create stories')}
                    <div className={styles.subLabel}>
                      {t('Group publications into key topics.')}
                    </div>
                  </label>
                  <Switch
                    id="create-stories"
                    value={narrativeCreateStories}
                    onChange={setNarrativeCreateStories}
                  />
                </div>
              ) : (
                ''
              )}
              {aspectBasedSentimentAvailable ? (
                <>
                  <div className="form-element">
                    <label
                      htmlFor="is-aspect-based-sentiment-enabled"
                      className={styles.label}
                    >
                      <span className={styles.labelIcon}>
                        <AIIcon />
                      </span>
                      {t('Contextual sentiment')}

                      <div className={styles.subLabel}>
                        {t(
                          'Detect contextual sentiments toward specific people, countries, events and more.',
                        )}
                      </div>
                    </label>
                    <Switch
                      id="is-aspect-based-sentiment-enabled"
                      value={narrativeIsAspectBasedSentimentEnabled}
                      onChange={setNarrativeIsAspectBasedSentimentEnabled}
                    />
                  </div>

                  {narrativeIsAspectBasedSentimentEnabled ? (
                    <>
                      <label
                        className={styles.label}
                        htmlFor="aspect-based-sentiment-object"
                      >
                        {t('Sentiment analysis')}
                      </label>
                      <Select
                        className={styles.sentimentSelect}
                        classNamePrefix="select-filter"
                        placeholder={t('Choose context for sentiment analysis')}
                        options={aspectBasedSentimentOptions}
                        id="aspect-based-sentiment-object"
                        name="aspect-based-sentiment-object"
                        value={narrativeAspectBasedSentimentObject}
                        onChange={(value) =>
                          setNarrativeAspectBasedSentimentObject(value)
                        }
                        menuPlacement="auto"
                      />

                      {Object.keys(sentimentAnalysisTitle).includes(
                        narrativeAspectBasedSentimentObject?.value,
                      ) ? (
                        <div className="form-element">
                          <label
                            htmlFor="aspect-based-sentiment-subject"
                            className={styles.label}
                          >
                            {
                              sentimentAnalysisTitle[
                                narrativeAspectBasedSentimentObject?.value
                              ]?.title
                            }
                            <div className={styles.subLabel}>
                              {' '}
                              {
                                sentimentAnalysisTitle[
                                  narrativeAspectBasedSentimentObject?.value
                                ].hint
                              }
                            </div>
                          </label>

                          <input
                            className=""
                            id="aspect-based-sentiment-subject"
                            name="aspect-based-sentiment-subject"
                            type="text"
                            onChange={(e) =>
                              setNarrativeAspectBasedSentimentSubject(
                                e.target.value,
                              )
                            }
                            required={narrativeIsAspectBasedSentimentEnabled}
                          />
                        </div>
                      ) : (
                        ''
                      )}
                    </>
                  ) : (
                    ''
                  )}
                </>
              ) : (
                ''
              )}
              {!state.startDate ? (
                <div className={styles.modalError}>
                  {t(
                    'You need to select a start date to be able to save the search results as a case',
                  )}
                </div>
              ) : (
                ''
              )}
              {narrativeIsAspectBasedSentimentEnabled &&
              narrativeAspectBasedSentimentObject !== null &&
              narrativeAspectBasedSentimentSubject.length > 0 &&
              narrativeAspectBasedSentimentSubject.trim().length === 0 ? (
                <div className={styles.modalError}>
                  {t(
                    'You need to add a subject for sentiment alalysis to be able to proceed with saving the case',
                  )}
                </div>
              ) : (
                ''
              )}
            </form>
          </div>
        }
        footer={
          <>
            <button
              className="new-button"
              onClick={(e) => {
                e.preventDefault();
                handleCreateNarrativ(
                  narrativeName,
                  narrativeDescription,
                  narrativeCreateStories,
                  narrativeIsAspectBasedSentimentEnabled,
                  narrativeAspectBasedSentimentObject,
                  narrativeAspectBasedSentimentSubject,
                  keywordsRef.current,
                  state.startDate,
                  state.endDate,
                  state.filters.platforms.value,
                  state.filters.sourceGroups.value,
                  state.filters.languages.value,
                  state.filters.sources.value,
                  state.filters.countries.value,
                  state.filters.sentiment.value,
                  state.filters.contentTypes.value,
                  state.filters.bots.value,
                  state.filters.discreditations.value,
                  state.filters.affiliations.value,
                  state.filters.feeds.value,
                  state.examples,
                  useMultilingualSearch,
                  isAIPoweredSearch,
                  similarTo,
                  state.filters.languages.exclude,
                  state.filters.countries.exclude,
                  state.filters.sourceGroups.exclude,
                  state.filters.sources.exclude,
                  state.filters.affiliations.exclude,
                  state.filters.feeds.exclude,
                  state.filters.geoGroup.value,
                  state.filters.geoGroup.exclude,
                  state.filters.bots.exclude,
                  state.filters.discreditations.exclude,
                  state.filters.affiliationTypes.value,
                  state.filters.affiliationTypes.exclude,
                );
              }}
              disabled={
                !(narrativeName.length > 0 && state.startDate) ||
                isQuotaChecking ||
                (narrativeIsAspectBasedSentimentEnabled &&
                  narrativeAspectBasedSentimentSubject.trim().length === 0)
              }
            >
              {isQuotaChecking ? t('Checking quota...') : t('Save')}
            </button>
            <button
              className="btn-reset"
              onClick={() => {
                setSaveModal(false);
                setNarrativeName('');
                setNarrativeDescription('');
                setNarrativeCreateStories(false);
                setNarrativeIsAspectBasedSentimentEnabled(false);
                setNarrativeAspectBasedSentimentObject(null);
                setNarrativeAspectBasedSentimentSubject('');
              }}
            >
              {t('Cancel')}
            </button>
          </>
        }
        onClose={() => {
          setSaveModal(false);
          setNarrativeName('');
          setNarrativeDescription('');
          setNarrativeCreateStories(false);
          setNarrativeIsAspectBasedSentimentEnabled(false);
          setNarrativeAspectBasedSentimentObject(null);
          setNarrativeAspectBasedSentimentSubject('');
        }}
      />

      <Modal
        isVisible={addToModal}
        title={t('Add to narrative')}
        doNotCloseOnOutsideClick={true}
        content={
          <div>
            <div className="modal-search">
              <Search
                className={styles.searchInput}
                onChange={(value) => setSearchQuery(value)}
              />
            </div>
            <ul className={styles.modalList}>
              {narratives?.objects.map((narrative) => {
                return (
                  <li
                    className={
                      narrative.id === selectedNarrative
                        ? `${styles.modalListItem} ${styles.selected}`
                        : styles.modalListItem
                    }
                    key={narrative.id}
                    onClick={() => {
                      if (narrative.id === selectedNarrative) {
                        setSelectedNarrative(null);
                      } else {
                        setSelectedNarrative(narrative.id);
                      }
                    }}
                  >
                    <span>{narrative.id}</span>
                    <span>{narrative.name}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        }
        footer={
          <div className={styles.footerWrapper}>
            <div className={styles.modalCheckbox}>
              <input
                id="save"
                type="checkbox"
                checked={isSaveSearchQuery}
                onChange={() => setIsSaveSearchQuery(!isSaveSearchQuery)}
              />
              <label htmlFor="save">Save search query in the narrative</label>
            </div>

            <div className={styles.ModalControls}>
              <button
                className="new-button"
                onClick={() => {
                  handleAddToNarrativ();
                  setAddToModal(false);
                }}
                disabled={selectedNarrative ? false : true}
              >
                {t('Save')}
              </button>
              <button
                className="btn-reset"
                onClick={() => {
                  setAddToModal(false);
                }}
              >
                {t('Cancel')}
              </button>
            </div>
          </div>
        }
        onClose={() => {
          setAddToModal(false);
        }}
      />

      {messageModal.isOpen ? (
        <MessageModal
          message={messageModal.message}
          isOpen={messageModal.isOpen}
          showSentimentIcon={false}
          onChange={(bool, selected) => {
            setMessageModal({
              message: null,
              isOpen: bool,
              narrartiveMessage: null,
            });
            setModalHighlights(selected);
          }}
          narrativeMessage={messageModal.narrartiveMessage}
          selected={modalHighlights.length === 0 ? highlights : modalHighlights}
        />
      ) : (
        ''
      )}

      {addToNarrativeModal ? (
        <AddToNarrativeModal
          isOpen={addToNarrativeModal}
          messages={selectedMessages}
          onChange={(bool) => {
            setAddToNarrativeModal(bool);
            setSelectedMessages({});
          }}
          isSearch={true}
        />
      ) : (
        ''
      )}

      {addToSourceGroupModal ? (
        <AddToSourceGroupModal
          isOpen={addToSourceGroupModal}
          messages={selectedMessages}
          allMessages={messages.objects}
          isSearch={true}
          onChange={(bool) => {
            setAddToSourceGroupModal(bool);
            setSelectedMessages({});
          }}
        />
      ) : (
        ''
      )}
      {getQuotaModalComponent()}
      {/* {isQueryAssistantModalOpen &&
        <QueryAssistantModal
          isVisible={isQueryAssistantModalOpen}
          onClose={() => setIsQueryAssistantModalOpen(false)}
          initialQuery={keywordsRef.current}
          session={queryAssistantSession}
          onSessionUpdate={(session) => setQueryAssistantSession(session)}
          onInsertQuery={(newQuery) => {
            onKeywordsChange(newQuery, null);
            if(queryInputRef.current){
              queryInputRef.current.set(newQuery);
            }
            setIsQueryAssistantModalOpen(false);
          }}

        />
      } */}
      <QueryAssistantModal
        isVisible={isQueryAssistantModalOpen}
        onClose={() => setIsQueryAssistantModalOpen(false)}
        initialQuery={keywordsRef.current}
        isInitialQueryValid={isValidQuery}
        session={queryAssistantSession}
        onSessionUpdate={(session) => setQueryAssistantSession(session)}
        onInsertQuery={(newQuery) => {
          onKeywordsChange(newQuery, null);
          if (queryInputRef.current) {
            queryInputRef.current.set(newQuery);
          }
          setIsQueryAssistantModalOpen(false);
        }}
      />
    </div>
  );
}
